import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Animacao } from '../../core/tools/animacao';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, from, of } from 'rxjs';
import { concatMap, toArray, tap, map } from 'rxjs/operators';

/* Services */

import { ClienteService } from '../../core/services/clientes/cliente.service';
import { NotaFiscalService } from '../../core/services/notas-fiscais/nota-fiscal.service';
import { ProdutoService } from '../../core/services/produtos/produto.service';
import { PromocoesService } from '../../core/services/produtos/promocoes.service';
import { VendedorService } from '../../core/services/vendedores/vendedor.service';
import { ExcelService } from '../../core/tools/excel.service';
import { ConfiguracaoService } from '../../core/services/configuracoes/configuracao.service';
import { PrazoPagamentoService } from '../../core/services/prazos-pagamento/prazo-pagamento.service';
import { PrazoPagamento } from '../../core/models/prazos-pagamento/prazo-pagamento';

/* Models */

import { Cliente } from '../../core/models/clientes/cliente';
import { NotaFiscalSintetica } from '../../core/models/notas-fiscais/nota-fiscal-sintetica';
import { Produto } from '../../core/models/produtos/produto';
import { ProdutoEspecialCliente } from '../../core/models/produtos/produto-especial-cliente';
import { ProdutoEspecialVendedor } from '../../core/models/produtos/produto-especial-vendedor';
import { ProdutoLote } from '../../core/models/produtos/produto-lote';
import { ProdutoTabelaEspecial } from '../../core/models/produtos/produto-tabela-especial';
import { ProdutoTabelaEspecialItens } from '../../core/models/produtos/produto-tabela-especial-itens';
import { ImportacaoPromocaoLinha } from '../../core/models/produtos/importacao-promocao-linha';
import { ImportacaoPromocaoClienteLinha } from '../../core/models/produtos/importacao-promocao-cliente-linha';
import { PromoRegraGrupo } from '../../core/models/produtos/promo-regra-grupo';
import { CotaPromocaoUsoLog } from '../../core/models/produtos/cota-promocao-uso-log';
import { Vendedor } from '../../core/models/vendedores/vendedor';

/* Interface */

export interface FiltroAtivo {
  tipo: string;
  valor: string;
}

@Component({
    selector: 'app-produtos-promocoes-cadastro',
    templateUrl: './produtos-promocoes-cadastro.component.html',
    styleUrls: ['./produtos-promocoes-cadastro.component.css'],
    preserveWhitespaces: false,
    animations: [Animacao],
    standalone: false
})

export class ProdutosPromocoesCadastroComponent implements OnInit {
  filtroAtivoImportados: string = 'todos';
  /* Carregamento */

  carregando: boolean = false;

  /* Itens (Menu) */

  itemSelecionado: number = 0;
  itemMaximoPermitido: number = 4;
  regraPrazoHabilitada = false;
  prazosPagamento: PrazoPagamento[] = [];
  prazosVinculados: number[] = [];
  prazoSelecionado: number = null;
  prazosCarregando = false;
  get prazosDisponiveis() { return this.prazosPagamento.filter(p => !this.prazosVinculados.includes(p.idprazopagamento)); }
  descricaoPrazo(id: number) { return this.prazosPagamento.find(p => p.idprazopagamento === id)?.descricao || 'Prazo ' + id + ' (indisponível para nova seleção)'; }
  adicionarPrazo() {
    const id = Number(this.prazoSelecionado);
    if (!this.regraPrazoHabilitada || !this.prazosDisponiveis.some(p => p.idprazopagamento === id)) return;
    this.prazosVinculados.push(id); this.prazoSelecionado = null;
  }
  removerPrazo(id: number) { if (this.regraPrazoHabilitada) this.prazosVinculados = this.prazosVinculados.filter(p => p !== id); }

  /* Regras de Grupo */
  private _nextTempId: number = -1;
  regrasGrupo: PromoRegraGrupo[] = [];
  regrasGrupoCarregando: boolean = false;
  rgCampoCliente: string = '';
  rgValorCliente: string = '';
  rgValoresCliente: string[] = [];
  rgValoresClienteCarregando: boolean = false;
  rgCampoProduto: string = '';
  rgValorProduto: string = '';
  rgValoresProduto: string[] = [];
  rgValoresProdutoCarregando: boolean = false;
  rgQtdProduto:        number = null;
  rgTipoProduto:       string = '';
  rgPercentualProduto: number = null;

  readonly camposCliente = [
    { campo: 'segment',        label: 'Segmento' },
    { campo: 'classificacao',  label: 'Classificação' },
    { campo: 'ramo_atividade', label: 'Ramo de Atividade' },
    { campo: 'grupo_regiao',   label: 'Grupo Região' },
    { campo: 'subsegmento',    label: 'Subsegmento' },
    { campo: 'subsegmento2',   label: 'Subsegmento 2' },
    { campo: 'tipo_fiscal',    label: 'Tipo Fiscal' },
    { campo: 'rede_postos',    label: 'Rede de Postos' },
  ];

  readonly camposProduto = [
    { campo: 'grupo_estoque',     label: 'Grupo de Estoque' },
    { campo: 'subgrupo_estoque',  label: 'Subgrupo 1' },
    { campo: 'subgrupo_estoque2', label: 'Subgrupo 2' },
    { campo: 'subgrupo_estoque3', label: 'Subgrupo 3' },
    { campo: 'subgrupo_estoque4', label: 'Subgrupo 4' },
    { campo: 'subgrupo_estoque5', label: 'Subgrupo 5' },
  ];

  /* Filtro (Clientes) */

  formFiltroCliente: UntypedFormGroup;

  filtroClientes: any[] = [
    { texto: "Código", valor: "codigo" },
    { texto: "CNPJ/CPF", valor: "cnpj_cpf" },
    { texto: "Nome Fantasia", valor: "nome_fantasia" },
    { texto: "Razão Social", valor: "razao_social" },
    { texto: "Responsável", valor: "responsavel" },
    { texto: "Cidade", valor: "cidade" },
    { texto: "Classificação", valor: "classificacao" }
  ];

  enviadoCliente: boolean = false;

  filtrandoCliente: boolean = false;

  /* Filtros Ativos Múltiplos (Clientes) */

  filtrosAtivosClientes: FiltroAtivo[] = [];

  clientesSelecionadosEmMassa: Set<number> = new Set();

  /* Filtro (Produtos) */

  formFiltroProduto: UntypedFormGroup;

  filtroProdutos: any[] = [
    { texto: "Código", valor: "codigo" },
    { texto: "Descrição", valor: "descricao" },
    { texto: "Marca", valor: "marca" },
    { texto: "Subgrupo 1", valor: "subgrupo1" },
    { texto: "Subgrupo 2", valor: "subgrupo2" },
    { texto: "Subgrupo 3", valor: "subgrupo3" },
    { texto: "Subgrupo 4", valor: "subgrupo4" },
    { texto: "Subgrupo 5", valor: "subgrupo5" }
  ];

  enviadoProduto: boolean = false;

  filtrandoProduto: boolean = false;

  /* Filtros Ativos (Múltiplos) */

  filtrosAtivos: FiltroAtivo[] = [];
  marcasDisponiveis: string[] = [];
  subgrupos1Disponiveis: string[] = [];
  subgrupos2Disponiveis: string[] = [];
  subgrupos3Disponiveis: string[] = [];
  subgrupos4Disponiveis: string[] = [];
  subgrupos5Disponiveis: string[] = [];

  /* Filtro (Vendedores) */

  formFiltroVendedor: UntypedFormGroup;

  filtroVendedores: any[] = [
    { texto: "Código", valor: "codigo" },
    { texto: "Nome", valor: "nome" },
    { texto: "Empresa", valor: "empresa" }
  ];

  enviadoVendedor: boolean = false;

  filtrandoVendedor: boolean = false;

  /* Filtros Ativos Múltiplos (Vendedores) */

  filtrosAtivosVendedores: FiltroAtivo[] = [];

  vendedoresSelecionadosEmMassa: Set<number> = new Set();
  empresasDisponiveis: string[] = [];

  /* Filtros (Clientes - Segmentação) */

  opcoesPorCampoCliente: { [campo: string]: string[] } = {};

  /* Formulário (Tabela Especial (Itens)) */

  formProdutoTabelaEspecialItem: UntypedFormGroup;

  enviadoProdutoTabelaEspecialItem: boolean = false;

  /* Formulário (Tabela Especial) */

  formProdutoTabelaEspecial: UntypedFormGroup;

  enviadoProdutoTabelaEspecial: boolean = false;

  /* Dados */

    /* Filtro (Clientes) */

    clientes: Cliente[] = [];
    clientesOriginal: Cliente[] = [];

    clienteSelecionado: Cliente = null;

    clienteInvalido: boolean = false;

    /* Debug */
    console = console;

    /* Filtro (Produtos) */

    produtoEmBranco: boolean = false;
    produtoInvalido: boolean = false;

    produtos: Produto[] = [];
    produtosOriginal: Produto[] = [];

    produtoSelecionado: Produto = null;

    /* Produtos (Seleção Múltipla para Importação em Massa) */

    produtosSelecionadosEmMassa: Set<number> = new Set();
    produtosSelecionadosDetalhes: Produto[] = []; // Cache dos produtos selecionados com detalhes
    private produtosPorId: Map<number, Produto> = new Map(); // Ýndice para busca rápida
    modoImportacaoEmMassa: boolean = true; // Sempre em modo de importação em massa
    percentualProdutos: number = 0;
    quantidadeMinimaProdutos: number = 0;
    tipoCalculo: string = 'margem'; // 'margem' ou 'desconto'

    /* Importação via planilha */
    @ViewChild('modalImportacaoPlanilha') modalImportacaoPlanilha: TemplateRef<any>;
    arquivoImportacao: File = null;
    linhasImportacao: ImportacaoPromocaoLinha[] = [];
    importandoPlanilha: boolean = false;

    @ViewChild('modalImportacaoClientesPlanilha') modalImportacaoClientesPlanilha: TemplateRef<any>;
    arquivoImportacaoClientes: File = null;
    linhasImportacaoClientes: ImportacaoPromocaoClienteLinha[] = [];
    importandoClientesPlanilha: boolean = false;


    /* Filtro (Vendedores) */

    vendedores: Vendedor[] = [];
    vendedoresOriginal: Vendedor[] = [];

    vendedorInvalido: boolean = false;

    vendedorSelecionado: Vendedor = null;

    /* Notas Fiscais */

    notasFiscais: NotaFiscalSintetica[] = [];

    /* Produto Especial (Clientes) */

    produtosEspecialClientes: ProdutoEspecialCliente[] = [];
    clientesRemovidosPendentes: ProdutoEspecialCliente[] = [];

    /* Produto Especial (Vendedores) */

    produtosEspecialVendedores: ProdutoEspecialVendedor[] = [];

    /* Produtos (Lotes) */

    produtosLotes: ProdutoLote[] = [];

    produtoLoteErro: boolean = false;

    /* Produto (Tabela Especial (Itens)) */

    produtoTabelaEspecialItemAtivo: boolean = true;

    produtosTabelaEspecialItens: ProdutoTabelaEspecialItens[] = [];

    produtoTabelaEspecialItemSelecionado: ProdutoTabelaEspecialItens = null;

    /* Produto (Tabela Especial) */

    produtoTabelaEspecialAtivo: boolean = true;

    naoAplicaFatorPontuacaoFinGlobal: boolean = false;

    eTabelaEspecial: boolean = false;

    produtoTabelaEspecial: ProdutoTabelaEspecial = null;

  /* Lote */

  exibirLote: boolean = false;

  /* Modal */

  modalReferenciaSelecionarLote: NgbModalRef = null;

  /* Log de uso da cota (promoção) */

  @ViewChild('modalLogItem') modalLogItem: TemplateRef<any>;
  @ViewChild('modalDiagnostico') modalDiagnostico: TemplateRef<any>;

  // Log individual do item
  logItemProdutoNome: string = "";
  logItemLinhas: CotaPromocaoUsoLog[] = [];
  carregandoLogItem: boolean = false;

  // Diagnóstico da promoção (carrega tudo e filtra client-side)
  diagnosticoLinhas: CotaPromocaoUsoLog[] = [];
  diagnosticoFiltradas: CotaPromocaoUsoLog[] = [];
  diagnosticoVendedores: { id: number; nome: string }[] = [];
  diagnosticoClientes: { id: number; nome: string }[] = [];
  diagFiltroSeller: number | null = null;
  diagFiltroClient: number | null = null;
  carregandoDiagnostico: boolean = false;

  /* Storage */

  guidclientenapis: string = localStorage.getItem("guidclientenapis");
  obs_gerais: string = localStorage.getItem("obs_gerais");

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private clienteService: ClienteService,
    private notaFiscalService: NotaFiscalService,
    private produtoService: ProdutoService,
    private promocoesService: PromocoesService,
    private vendedorService: VendedorService,
    private excelService: ExcelService,
    private configuracaoService: ConfiguracaoService,
    private prazoPagamentoService: PrazoPagamentoService
  ) { }

  ngOnInit() {
    this.configuracaoService.getByGuidClienteNapis().subscribe(config => {
      this.regraPrazoHabilitada = config?.prazo_pagamento_antes_itenspedido === true;
      if (!this.regraPrazoHabilitada) return;
      this.prazosCarregando = true;
      this.prazoPagamentoService.getByGuidClienteNapis().subscribe({
        next: rows => this.prazosPagamento = rows || [],
        error: () => this.prazosCarregando = false,
        complete: () => this.prazosCarregando = false
      });
    });
    /* Filtro (Clientes) */

    this.formFiltroCliente = this.formBuilder.group({
      filtro: ["codigo"],
      valor: ["", Validators.required]
    });

    /* Filtro (Produtos) */

    this.formFiltroProduto = this.formBuilder.group({
      filtro: ["descricao"],
      valor: ["", Validators.required]
    });

    this.organizarFiltroProduto();

    /* Filtro (Vendedores) */

    this.formFiltroVendedor = this.formBuilder.group({
      filtro: ["codigo"],
      valor: ["", Validators.required]
    });

    this.formProdutoTabelaEspecialItem = this.formBuilder.group({
      qtde: [{ value: "", disabled: true }],
      preco: [{ value: "", disabled: true }, Validators.required],
      cota: [{ value: "", disabled: true }]
    });

    this.formProdutoTabelaEspecial = this.formBuilder.group({
      nome: ["", Validators.required],
      dt_inicio: [null],
      dt_fim: [null]
    });

    let vincular: boolean = false;

    this.route.queryParams.subscribe(parametros => {
      vincular = parametros["vincular"];
      if (parametros["tabelaEspecial"] !== undefined) {
        this.eTabelaEspecial = parametros["tabelaEspecial"] === 'true';
        this.naoAplicaFatorPontuacaoFinGlobal = this.eTabelaEspecial;
      }
    });

    if (vincular) {
      this.itemSelecionado = 2;
      this.itemMaximoPermitido = 3;
    }

    this.carregarDados();
  }

  get fc() {
    return this.formFiltroCliente.controls;
  }

  get fv() {
    return this.formFiltroVendedor.controls;
  }

  get ffp() {
    return this.formFiltroProduto.controls;
  }

  get fti() {
    return this.formProdutoTabelaEspecialItem.controls;
  }

  get fta() {
    return this.formProdutoTabelaEspecial.controls;
  }

  get margemCalculada(): number | null {
    if (this.produtoSelecionado == null) return null;
    const custo = this.produtoSelecionado.custo;
    const precoControl = this.formProdutoTabelaEspecialItem != null ? this.formProdutoTabelaEspecialItem.get('preco') : null;
    const precoPromocao = precoControl != null ? Number(precoControl.value) : 0;
    if (!custo || custo <= 0 || !precoPromocao || precoPromocao <= 0) return null;
    const m = (1 - (custo / precoPromocao)) * 100;
    return Math.round(m * 100) / 100;
  }

  /* Ações */

  reiniciarProdutoTabelaEspecial() {
    /* Itens (Menu) */

    this.itemSelecionado = 0;
    this.itemMaximoPermitido = 0;

    /* Formulário (Tabela Especial) */

    this.formProdutoTabelaEspecial.reset();

    this.enviadoProdutoTabelaEspecial = false;

    /* Produto (Tabela Especial) */

    this.produtoTabelaEspecialAtivo = true;

    this.produtoTabelaEspecial = null;
    this.prazosVinculados = [];
    this.prazoSelecionado = null;

    /* Produto (Tabela Especial (Itens)) */

    this.produtosTabelaEspecialItens = [];

    this.reiniciarProdutoTabelaEspecialItem();
  }

  reiniciarProdutoTabelaEspecialItem() {
    /* Formulário (Tabela Especial (Itens)) */

    this.formProdutoTabelaEspecialItem.reset()

    this.formProdutoTabelaEspecialItem.controls["qtde"].disable();
    this.formProdutoTabelaEspecialItem.controls["preco"].disable();

    this.enviadoProdutoTabelaEspecialItem = false;

    /* Filtro (Produtos) */

    this.produtoEmBranco = false;
    this.produtoInvalido = false;

    this.produtoSelecionado = null;

    /* Produto (Tabela Especial (Itens)) */

    this.produtoTabelaEspecialItemAtivo = true;

    this.produtoTabelaEspecialItemSelecionado = null;

    /* Produto (Lote) */

    this.removerLoteProduto();
  }

  salvarProdutoTabelaEspecial() {
    // Validar formulário de informações
    this.enviadoProdutoTabelaEspecial = true;

    if (this.formProdutoTabelaEspecial.invalid) {
      this.toastr.error("", "Informações inválidas!");
      return;
    }

    this.carregando = true;

    const dtInicio = this.formProdutoTabelaEspecial.get('dt_inicio').value;
    const dtFim = this.formProdutoTabelaEspecial.get('dt_fim').value;

    this.produtosTabelaEspecialItens.forEach((item) => {
      item.dt_inicio = dtInicio;
      item.dt_fim = dtFim;
      if (this.produtoTabelaEspecial != null && this.produtoTabelaEspecial.idprodutotabelaespecial != null) {
        item.idprodutotabelaespecial = this.produtoTabelaEspecial.idprodutotabelaespecial;
      }
    });

    this.prepararEnviarDados();

    if (this.produtoTabelaEspecial != null && this.produtoTabelaEspecial.idprodutotabelaespecial != null) {
      this.salvarAlteracao();
    } else {
      this.salvarNovaPromoção();
    }
  }

  private salvarAlteracao() {
    let alterarProdutoTabelaEspecial: boolean = false;

    this.produtoService.updateProdutoTabelaEspecial(this.produtoTabelaEspecial).subscribe(
      resultado => alterarProdutoTabelaEspecial = resultado,
      error => {
        console.log("Erro ao alterar promoção: " + error);
        this.carregando = false;
      },
      () => {
        if (alterarProdutoTabelaEspecial) {
          let alterarItens: boolean = false;
          this.produtoService.updateProdutoTabelaEspecialItens(this.produtosTabelaEspecialItens).subscribe(
            resultado => alterarItens = resultado,
            error => {
              console.log("Erro ao atualizar itens: " + error);
              this.carregando = false;
            },
            () => {
              if (alterarItens) {
                this.persistirClientesEVendedoresVinculados(true);
              } else {
                this.carregando = false;
                this.toastr.error("", "Houve um erro ao atualizar itens, contate o suporte...");
              }
            }
          );
        } else {
          this.carregando = false;
          this.toastr.error("", "Houve um erro ao alterar a promoção, contate o suporte...");
        }
      }
    );
  }

  private salvarNovaPromoção() {
    let adicionarProdutoTabelaEspecial: number = 0;

    this.produtoService.addProdutoTabelaEspecial(this.produtoTabelaEspecial).subscribe(
      resultado => adicionarProdutoTabelaEspecial = resultado,
      error => {
        console.log("Erro ao adicionar promoção: " + error);
        this.carregando = false;
      },
      () => {
        if (adicionarProdutoTabelaEspecial > 0) {
          this.produtosTabelaEspecialItens.forEach((item) => {
            item.idprodutotabelaespecial = adicionarProdutoTabelaEspecial;
          });

          let alterarItens: boolean = false;
          this.produtoService.updateProdutoTabelaEspecialItens(this.produtosTabelaEspecialItens).subscribe(
            resultado => alterarItens = resultado,
            error => {
              console.log("Erro ao atualizar itens: " + error);
              this.carregando = false;
            },
            () => {
              if (alterarItens) {
                // Atualizar id dos clientes e vendedores com o id da promoção criada
                this.produtosEspecialClientes.forEach((cliente) => {
                  cliente.idprodutotabelaespecial = adicionarProdutoTabelaEspecial;
                });
                this.produtosEspecialVendedores.forEach((vendedor) => {
                  vendedor.idprodutotabelaespecial = adicionarProdutoTabelaEspecial;
                });

                this.persistirRegrasGrupo(adicionarProdutoTabelaEspecial).subscribe({
                  complete: () => this.persistirClientesEVendedoresVinculados(false, adicionarProdutoTabelaEspecial),
                  error: () => this.persistirClientesEVendedoresVinculados(false, adicionarProdutoTabelaEspecial)
                });
              } else {
                this.carregando = false;
                this.toastr.error("", "Houve um erro ao atualizar itens, contate o suporte...");
              }
            }
          );
        } else {
          this.carregando = false;
          this.toastr.error("", "Houve um erro ao adicionar a promoção, contate o suporte...");
        }
      }
    );
  }

  private persistirClientesEVendedoresVinculados(isAlteracao: boolean, idNovaPromoção?: number) {
    const idprodutotabelaespecial = idNovaPromoção != null ? idNovaPromoção : this.produtoTabelaEspecial.idprodutotabelaespecial;
    this.persistirClientesVinculados().subscribe(
      () => {
        this.persistirVendedoresVinculados(idprodutotabelaespecial).subscribe(
          () => {
            this.carregando = false;
            if (isAlteracao) {
              this.router.navigate(["/produtos"]);
              this.toastr.success("", "Promoção alterada com sucesso!");
            } else {
              this.router.navigate(["/produtos-promocoes-cadastro/" + idNovaPromoção], {
                queryParams: { vincular: 1 }
              });
              this.toastr.success("", "Promoção adicionada com sucesso!");
            }
          },
          error => {
            console.log("Erro ao persistir vendedores: " + error);
            this.carregando = false;
            this.toastr.warning("", "Promoção salva, mas houve erro ao vincular vendedores.");
          }
        );
      },
      error => {
        console.log("Erro ao persistir clientes: " + error);
        this.carregando = false;
        this.toastr.warning("", "Promoção salva, mas houve erro ao vincular clientes.");
      }
    );
  }

  onClientesRemovidos(clientesRemovidos: ProdutoEspecialCliente[]) {
    if (clientesRemovidos == null || clientesRemovidos.length === 0) {
      return;
    }

    const idsClientesRemovidos = new Set(clientesRemovidos.map(c => c.idcliente));

    this.produtosEspecialClientes = this.produtosEspecialClientes.filter(
      cliente => !idsClientesRemovidos.has(cliente.idcliente)
    );

    // Só precisa ir ao backend quem já está persistido; os pendentes basta sumir da lista
    const jaPersistidos = clientesRemovidos.filter(c => c.idprodutoespecialcliente > 0);

    this.clientesRemovidosPendentes = [...this.clientesRemovidosPendentes, ...jaPersistidos];
  }

  private persistirClientesVinculados(): Observable<void> {
    return this.excluirClientesRemovidos().pipe(
      concatMap(() => this.adicionarClientesPendentes())
    );
  }

  private excluirClientesRemovidos(): Observable<void> {
    if (this.clientesRemovidosPendentes.length === 0) {
      return of(void 0);
    }

    const clientesParaExcluir = this.clientesRemovidosPendentes;

    return from(clientesParaExcluir).pipe(
      concatMap(cliente => this.produtoService.deleteProdutoEspecialClienteById(cliente.idprodutoespecialcliente)),
      toArray(),
      tap(() => {
        this.clientesRemovidosPendentes = this.clientesRemovidosPendentes.filter(
          cliente => clientesParaExcluir.indexOf(cliente) === -1
        );
      }),
      map(() => void 0)
    );
  }

  private adicionarClientesPendentes(): Observable<void> {
    const clientesPendentes = this.produtosEspecialClientes.filter(c => !c.idprodutoespecialcliente || c.idprodutoespecialcliente === 0);

    if (clientesPendentes.length === 0) {
      return of(void 0);
    }

    return from(clientesPendentes).pipe(
      concatMap(cliente =>
        this.produtoService.addProdutoEspecialCliente(cliente).pipe(
          tap(idRetornado => {
            cliente.idprodutoespecialcliente = idRetornado;
          })
        )
      ),
      toArray(),
      map(() => void 0)
    );
  }

  private persistirVendedoresVinculados(idprodutotabelaespecial: number): Observable<void> {
    const idVendedores = this.produtosEspecialVendedores.map(v => v.idvendedor);

    return this.produtoService.syncProdutoEspecialVendedores(idprodutotabelaespecial, idVendedores).pipe(
      tap(vendedoresAtualizados => {
        this.produtosEspecialVendedores = vendedoresAtualizados;
      }),
      map(() => void 0)
    );
  }

  salvarProdutoTabelaEspecialItem() {
    console.log("%c[DBG salvarItem] >>> clique CHEGOU no handler", "color:#2980b9;font-weight:bold");
    this.enviadoProdutoTabelaEspecialItem = true;

    /* Filtro (Produtos) */

    this.produtoEmBranco = false;
    this.produtoInvalido = false;

    /* Produtos (Lotes) */

    this.produtoLoteErro = false;

    if (this.produtoSelecionado == null) {
      this.produtoEmBranco = true;

      this.toastr.error("", "Produto em branco!");
    } else {
      if (this.produtoSelecionado.qtde_lote > 0) {
        if (this.produtoSelecionado.id_lote == null) {
          this.produtoLoteErro = true;

          this.toastr.error("", "Selecione um lote para este produto!");
        }
      }
    }

    if (this.produtoSelecionado != null) {
      let qtde: number = this.formProdutoTabelaEspecialItem.controls["qtde"].value;

      if (qtde != 0 && qtde != null) {
        if (this.produtoSelecionado.multiplo != null && this.produtoSelecionado.multiplo != 0) {
          if (qtde % this.produtoSelecionado.multiplo != 0) {
            this.formProdutoTabelaEspecialItem.controls["qtde"].setErrors({ "invalid": true });
          }
        }
      }
    }

    if (this.formProdutoTabelaEspecialItem.controls["preco"].valid) {
      if (this.formProdutoTabelaEspecialItem.controls["preco"].value == 0) {
        this.formProdutoTabelaEspecialItem.controls["preco"].setErrors({ "invalid": true });
      }
    }

    const dbgQtde = this.formProdutoTabelaEspecialItem.controls["qtde"];
    const dbgPreco = this.formProdutoTabelaEspecialItem.controls["preco"];
    const dbgCota = this.formProdutoTabelaEspecialItem.controls["cota"];
    console.log("%c[DBG salvarItem]", "color:#c0392b;font-weight:bold", {
      passaGate: !this.produtoEmBranco && !this.produtoLoteErro && this.formProdutoTabelaEspecialItem.valid,
      produtoEmBranco: this.produtoEmBranco,
      produtoInvalido: this.produtoInvalido,
      produtoLoteErro: this.produtoLoteErro,
      form_valid: this.formProdutoTabelaEspecialItem.valid,
      qtde: { value: dbgQtde.value, status: dbgQtde.status, disabled: dbgQtde.disabled, errors: dbgQtde.errors },
      preco: { value: dbgPreco.value, status: dbgPreco.status, disabled: dbgPreco.disabled, errors: dbgPreco.errors },
      cota: { value: dbgCota.value, status: dbgCota.status, disabled: dbgCota.disabled, errors: dbgCota.errors },
      produtoSelecionado: this.produtoSelecionado == null ? null : {
        codigo: this.produtoSelecionado.codigo,
        qtde_lote: this.produtoSelecionado.qtde_lote,
        id_lote: this.produtoSelecionado.id_lote,
        valor: this.produtoSelecionado.valor
      },
      itemSelecionado: this.produtoTabelaEspecialItemSelecionado == null ? null : {
        idprodutoespecial: this.produtoTabelaEspecialItemSelecionado.idprodutoespecial,
        codigo: this.produtoTabelaEspecialItemSelecionado.codigo,
        qtd_minima: this.produtoTabelaEspecialItemSelecionado.qtd_minima,
        id_lote: this.produtoTabelaEspecialItemSelecionado.id_lote,
        qtde_lote: this.produtoTabelaEspecialItemSelecionado.qtde_lote
      }
    });

    if (this.produtoEmBranco || this.produtoLoteErro || !this.formProdutoTabelaEspecialItem.valid) {
      console.warn("%c[DBG salvarItem] BLOQUEADO — motivo:", "color:#e67e22;font-weight:bold",
        this.produtoEmBranco ? "produtoEmBranco" :
        this.produtoLoteErro ? "produtoLoteErro (lote obrigatorio, id_lote nulo)" :
        "form invalido -> " + JSON.stringify({ qtde: dbgQtde.errors, preco: dbgPreco.errors, cota: dbgCota.errors }));
    }

    if (!this.produtoEmBranco && !this.produtoLoteErro && this.formProdutoTabelaEspecialItem.valid) {
      let qtde: number = this.formProdutoTabelaEspecialItem.controls["qtde"].value == null || this.formProdutoTabelaEspecialItem.controls["qtde"].value == "" ? 0 : this.formProdutoTabelaEspecialItem.controls["qtde"].value;

      let produtoAdicionado = this.produtosTabelaEspecialItens.find(produtoTabelaEspecialItem => produtoTabelaEspecialItem.codigo == this.produtoSelecionado.codigo && produtoTabelaEspecialItem.qtd_minima == qtde);

      if (produtoAdicionado != null) {
        if (this.produtoTabelaEspecialItemSelecionado != null) {
          /* Alterando */

          if (this.produtoTabelaEspecialItemSelecionado.codigo == produtoAdicionado.codigo && this.produtoTabelaEspecialItemSelecionado.qtd_minima == produtoAdicionado.qtd_minima) {

          } else {
            this.toastr.error("", "Produto/Qtde já adicionado!");

            this.produtoInvalido = true;
          }
        } else {
          /* Adicionando */

          this.toastr.error("", "Produto/Qtde já adicionado!");

          return;
        }
      }

      if (!this.produtoInvalido) {
        let produtoTabelaEspecialItens = new ProdutoTabelaEspecialItens;

        produtoTabelaEspecialItens.ativo = this.produtoTabelaEspecialItemAtivo;
        produtoTabelaEspecialItens.preco = this.formProdutoTabelaEspecialItem.controls["preco"].value;
        produtoTabelaEspecialItens.cota_total = this.formProdutoTabelaEspecialItem.controls["cota"].value || null;
        produtoTabelaEspecialItens.guidclientenapis = this.guidclientenapis;
        produtoTabelaEspecialItens.codigo = this.produtoSelecionado.codigo;
        produtoTabelaEspecialItens.qtd_minima = qtde;
        produtoTabelaEspecialItens.id_lote = this.produtoSelecionado.id_lote;

        /* Produto */

        produtoTabelaEspecialItens.name = this.produtoSelecionado.name;
        produtoTabelaEspecialItens.price = this.produtoSelecionado.valor;
        produtoTabelaEspecialItens.custo = this.produtoSelecionado.custo;

        /* Lote */

        produtoTabelaEspecialItens.nome_lote = this.produtoSelecionado.nome_lote;
        produtoTabelaEspecialItens.cod_lote = this.produtoSelecionado.cod_lote;
        produtoTabelaEspecialItens.estoque_lote = this.produtoSelecionado.estoque_lote;
        produtoTabelaEspecialItens.validade_lote = this.produtoSelecionado.validade_lote;
        produtoTabelaEspecialItens.preco_lote = this.produtoSelecionado.preco_lote;

        if (this.produtoTabelaEspecialItemSelecionado != null) {
          /* Alterando */

          let produtoAdicionado = this.produtosTabelaEspecialItens.findIndex(produtoTabelaEspecialItem => produtoTabelaEspecialItem.codigo == this.produtoTabelaEspecialItemSelecionado.codigo && produtoTabelaEspecialItem.qtd_minima == this.produtoTabelaEspecialItemSelecionado.qtd_minima);

          if (produtoAdicionado != -1) {
            this.produtosTabelaEspecialItens.splice(produtoAdicionado, 1);

            this.produtosTabelaEspecialItens = [...this.produtosTabelaEspecialItens];

            produtoTabelaEspecialItens.idprodutoespecial = this.produtoTabelaEspecialItemSelecionado.idprodutoespecial;

            this.produtosTabelaEspecialItens.push(produtoTabelaEspecialItens);

            this.organizarProdutoTabelaEspecialItem();
            this.sincronizarDatasFormComItensSeVazio();
            this.reiniciarProdutoTabelaEspecialItem();

            this.toastr.success("", "Produto alterado com sucesso!");
          }
        } else {
          /* Adicionando */

          produtoTabelaEspecialItens.idprodutoespecial = 0;

          if (this.produtoTabelaEspecial != null) {
            produtoTabelaEspecialItens.idprodutotabelaespecial = this.produtoTabelaEspecial.idprodutotabelaespecial;
          }

          this.produtosTabelaEspecialItens.push(produtoTabelaEspecialItens);

          this.organizarProdutoTabelaEspecialItem();
          this.sincronizarDatasFormComItensSeVazio();
          this.reiniciarProdutoTabelaEspecialItem();

          this.toastr.success("", "Produto adicionado com sucesso!");
        }

        this.verificarExibirLote();
      }
    }
  }

  selecionarProdutoTabelaEspecialItem(codigo: string, qtde: number) {
    this.produtoEmBranco = false;
    this.produtoInvalido = false;

    this.produtoTabelaEspecialItemSelecionado = this.produtosTabelaEspecialItens.find(produtoTabelaEspecialItem => produtoTabelaEspecialItem.codigo == codigo && produtoTabelaEspecialItem.qtd_minima == qtde);

    if (this.produtoTabelaEspecialItemSelecionado != null) {
      this.produtoSelecionado = this.produtos.find(produto => produto.codigo == this.produtoTabelaEspecialItemSelecionado.codigo);

      /* Fallback: produto não está nos resultados de pesquisa (busca ainda não realizada).
         Constrói produtoSelecionado a partir dos dados já carregados do backend. */
      if (this.produtoSelecionado == null) {
        const item = this.produtoTabelaEspecialItemSelecionado;
        this.produtoSelecionado = new Produto();
        this.produtoSelecionado.id = item.id_produto;
        this.produtoSelecionado.codigo = item.codigo;
        this.produtoSelecionado.name = item.name;
        this.produtoSelecionado.custo = item.custo;
        this.produtoSelecionado.price = item.price;
        /* qtde_lote real do produto (backend) — exibe o Lote sempre que o produto suporta lote,
           mesmo que este item ainda não tenha um lote atribuído (ex.: importado em massa). */
        this.produtoSelecionado.qtde_lote = item.qtde_lote;
      }

      if (this.produtoSelecionado != null) {
        this.produtoTabelaEspecialItemAtivo = this.produtoTabelaEspecialItemSelecionado.ativo;

        this.produtoSelecionado.valor = this.produtoTabelaEspecialItemSelecionado.price;

        /* Lote */

        this.produtoSelecionado.id_lote = this.produtoTabelaEspecialItemSelecionado.id_lote;
        this.produtoSelecionado.nome_lote = this.produtoTabelaEspecialItemSelecionado.nome_lote;
        this.produtoSelecionado.cod_lote = this.produtoTabelaEspecialItemSelecionado.cod_lote;
        this.produtoSelecionado.estoque_lote = this.produtoTabelaEspecialItemSelecionado.estoque_lote;
        this.produtoSelecionado.validade_lote = this.produtoTabelaEspecialItemSelecionado.validade_lote;
        this.produtoSelecionado.preco_lote = this.produtoTabelaEspecialItemSelecionado.preco_lote;

        /* Quando o produto veio do catálogo (this.produtos), preserva o qtde_lote real dele;
           quando veio do fallback, já foi setado a partir de item.qtde_lote. Garante o valor real
           do item salvo caso o catálogo não traga essa contagem. */
        if (!(this.produtoSelecionado.qtde_lote > 0) && this.produtoTabelaEspecialItemSelecionado.qtde_lote > 0) {
          this.produtoSelecionado.qtde_lote = this.produtoTabelaEspecialItemSelecionado.qtde_lote;
        }

        /* Formulário (Tabela Especial (Itens)) */

        this.formProdutoTabelaEspecialItem.controls["qtde"].enable();
        this.formProdutoTabelaEspecialItem.controls["qtde"].setValue(this.produtoTabelaEspecialItemSelecionado.qtd_minima);

        this.formProdutoTabelaEspecialItem.controls["preco"].enable();
        this.formProdutoTabelaEspecialItem.controls["preco"].setValue(this.produtoTabelaEspecialItemSelecionado.preco);

        this.formProdutoTabelaEspecialItem.controls["cota"].enable();
        this.formProdutoTabelaEspecialItem.controls["cota"].setValue(this.produtoTabelaEspecialItemSelecionado.cota_total);

        console.log("%c[DBG editarItem] item aberto para edicao", "color:#8e44ad;font-weight:bold", {
          codigo: this.produtoSelecionado.codigo,
          veioDoCatalogo: this.produtos.some(p => p.codigo == this.produtoTabelaEspecialItemSelecionado.codigo),
          produtoSelecionado_qtde_lote: this.produtoSelecionado.qtde_lote,
          produtoSelecionado_id_lote: this.produtoSelecionado.id_lote,
          item_qtde_lote: this.produtoTabelaEspecialItemSelecionado.qtde_lote,
          item_id_lote: this.produtoTabelaEspecialItemSelecionado.id_lote,
          diagnostico: this.produtoSelecionado.qtde_lote > 0 && this.produtoSelecionado.id_lote == null
            ? "LOTE SERA OBRIGATORIO no salvar (bloqueia se nao escolher lote)"
            : "lote nao obrigatorio"
        });
      }
    }
  }

  selecionarProdutoFiltrado(id_produto: number) {
    let produtoSelecionado = this.produtos.find(produtos => produtos.id == id_produto);

    if (produtoSelecionado != null) {
      this.produtoSelecionado = produtoSelecionado;

      if (this.produtoSelecionado.qtde_lote == 0) {
        this.produtoSelecionado.valor = this.produtoSelecionado.price;
      } else {
        this.produtoSelecionado.valor = null;
      }

      this.alterarFiltroProduto(null);
      this.removerLoteProduto();

      this.produtoEmBranco = false;
      this.produtoInvalido = false;
      this.filtrandoProduto = false;

      /* Formulário (Tabela Especial (Itens)) */

      if (produtoSelecionado.qtde_lote == 0) {
        this.formProdutoTabelaEspecialItem.controls["qtde"].enable();
        this.formProdutoTabelaEspecialItem.controls["preco"].enable();
      }

      /* Cota (independe de lote — sempre habilitada ao escolher o produto) */
      this.formProdutoTabelaEspecialItem.controls["cota"].enable();
      this.formProdutoTabelaEspecialItem.controls["cota"].setValue(null);
    }
  }

  /* Dados */

  carregarDados() {
    this.carregando = true;

    this.vendedorService.getByUsuario().subscribe(
      vendedores => this.vendedores = vendedores,
      error => { console.log("Erro: " + error) },
      () => {
        this.vendedoresOriginal = JSON.parse(JSON.stringify(this.vendedores));
        this.extrairEmpresasDisponiveis();

        // Carregar opções de filtro (DISTINCT values) sem carregar todos os dados
        forkJoin([
          this.produtoService.getFiltroOpcoes(),
          this.clienteService.getClienteFiltrosConfigurados()
        ]).subscribe(([opProdutos, configClientes]) => {
          this.marcasDisponiveis = opProdutos.marcas;
          this.subgrupos1Disponiveis = opProdutos.subgrupos1;
          this.subgrupos2Disponiveis = opProdutos.subgrupos2;
          this.subgrupos3Disponiveis = opProdutos.subgrupos3;
          this.subgrupos4Disponiveis = opProdutos.subgrupos4;
          this.subgrupos5Disponiveis = opProdutos.subgrupos5;

          // Construir opcoesPorCampoCliente e filtroClientes dinâmicos
          const filtrosFixos = [
            { texto: 'Código', valor: 'codigo' },
            { texto: 'CNPJ/CPF', valor: 'cnpj_cpf' },
            { texto: 'Nome Fantasia', valor: 'nome_fantasia' },
            { texto: 'Razão Social', valor: 'razao_social' },
            { texto: 'Responsável', valor: 'responsavel' },
          ];
          const filtrosDinamicos = (configClientes.filtros || [])
            .filter((f: any) => f.opcoes && f.opcoes.length > 0)
            .map((f: any) => ({ texto: f.label, valor: f.campo }));
          const filtrosExtras = [
            { texto: 'Cidade', valor: 'cidade' },
            { texto: 'Classificação', valor: 'classificacao' },
          ];
          this.filtroClientes = [...filtrosFixos, ...filtrosDinamicos, ...filtrosExtras];

          this.opcoesPorCampoCliente = {};
          (configClientes.filtros || []).forEach((f: any) => {
            this.opcoesPorCampoCliente[f.campo] = f.opcoes || [];
          });
          // Cidade e Classificação são tratadas como texto livre (sem opcoes da API)

          this.verificarAdicionandoAlterando();
        });
      }
    );
  }

  excluirProdutoEspecialCliente(id_produto_especial_cliente: number) {
    if (this.produtoTabelaEspecial != null) {
      let deletarProdutoEspecialCliente: boolean = false;

      this.produtoService.deleteProdutoEspecialClienteById(id_produto_especial_cliente).subscribe(
        deletarProdutoEspecialClientes => deletarProdutoEspecialCliente = deletarProdutoEspecialClientes,
        error => { console.log("Erro: " + error) },
        () => {
          if (deletarProdutoEspecialCliente) {
            let produtoProdutoEspecialCliente = this.produtosEspecialClientes.findIndex(produtoEspecialCliente => produtoEspecialCliente.idprodutoespecialcliente == id_produto_especial_cliente);

            if (produtoProdutoEspecialCliente != -1) {
              this.produtosEspecialClientes.splice(produtoProdutoEspecialCliente, 1);

              this.produtosEspecialClientes = [...this.produtosEspecialClientes];
            }

            this.toastr.success("", "Cliente excluído com sucesso!");
          }
        }
      );
    }
  }

  excluirProdutoEspecialVendedor(id_produto_especial_vendedor: number) {
    if (this.produtoTabelaEspecial != null) {
      let deletarProdutoEspecialVendedor: boolean = false;

      this.produtoService.deleteProdutoEspecialVendedorById(id_produto_especial_vendedor).subscribe(
        deletarProdutoEspecialVendedores => deletarProdutoEspecialVendedor = deletarProdutoEspecialVendedores,
        error => { console.log("Erro: " + error) },
        () => {
          if (deletarProdutoEspecialVendedor) {
            let produtoProdutoEspecialVendedor = this.produtosEspecialVendedores.findIndex(produtoEspecialVendedor => produtoEspecialVendedor.idprodutoespecialvendedor == id_produto_especial_vendedor);

            if (produtoProdutoEspecialVendedor != -1) {
              this.produtosEspecialVendedores.splice(produtoProdutoEspecialVendedor, 1);

              this.produtosEspecialVendedores = [...this.produtosEspecialVendedores];
            }

            this.toastr.success("", "Vendedor(a) excluída com sucesso!");
          }
        }
      );
    }
  }

  organizarProdutoTabelaEspecialItem() {
    this.produtosTabelaEspecialItens = this.produtosTabelaEspecialItens.sort((a, b) => {
      const compare = (v1, v2) => v1 == null ? -1 : v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

      return compare(a["name"], b["name"]);
    });

    this.produtosTabelaEspecialItens = this.produtosTabelaEspecialItens.sort((a, b) => {
      const compare = (v1, v2) => v1 == null ? -1 : v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

      return compare(a["qtd_minima"], b["qtd_minima"]);
    });
  }

  prepararReceberDados(id_produto_tabela_especial: number) {
    let retorno = new Promise<void>((resolver) => {
      /* Produto (Tabela Especial) */

      this.produtoService.getProdutoTabelaEspecialById(id_produto_tabela_especial).subscribe(
        produtoTabelaEspecial => {
          this.produtoTabelaEspecial = produtoTabelaEspecial;
          this.prazosVinculados = [...new Set((produtoTabelaEspecial?.id_prazostr || '').split(';').map(p => Number(p.trim())).filter(p => Number.isInteger(p) && p > 0))];
        },
        error => { console.log("Erro: " + error) },
        () => {
          if (this.produtoTabelaEspecial != null) {
            this.formProdutoTabelaEspecial.controls["nome"].setValue(this.produtoTabelaEspecial.descricao);
            this.formProdutoTabelaEspecial.get('dt_inicio').setValue(this.produtoTabelaEspecial.dt_inicio);
            this.formProdutoTabelaEspecial.get('dt_fim').setValue(this.produtoTabelaEspecial.dt_fim);
            this.produtoTabelaEspecial.idprodutotabelaespecial = id_produto_tabela_especial;

            this.produtoTabelaEspecialAtivo = this.produtoTabelaEspecial.ativo;
            this.naoAplicaFatorPontuacaoFinGlobal = this.produtoTabelaEspecial.flag_naoaplicafatorpontuacaofin === true;

            /* Produto (Tabela Especial (Itens)) */

            this.produtoService.getProdutoTabelaEspecialItensByIdProdutoTabelaEspecial(this.produtoTabelaEspecial.idprodutotabelaespecial).subscribe(
              produtosTabelaEspecialItens => this.produtosTabelaEspecialItens = produtosTabelaEspecialItens,
              error => { console.log("Erro: " + error) },
              () => {
                  if (this.produtosTabelaEspecialItens != null) {
                    this.produtosTabelaEspecialItens.forEach((item) => {
                      if (item.id_lote != null) {
                        item.price = item.preco_lote;
                      }

                      // Converter 'M' (margem) e 'D' (desconto) de volta para o formato UI
                      if (item.tipo === 'M') {
                        item.tipo_calculo = 'margem';
                      } else if (item.tipo === 'D') {
                        item.tipo_calculo = 'desconto';
                      }

                      // Mapear tipo_percentual para percentual (campo que a tabela usa)
                      if (item.tipo_percentual != null && !item.percentual) {
                        item.percentual = item.tipo_percentual;
                      }
                    });
                    // Sincronizar datas do formulário com os itens se estiver vazio
                    this.sincronizarDatasFormComItensSeVazio();
                  }

                  this.verificarExibirLote();

                /* Produto Especial (Clientes) */

                this.produtoService.getProdutoEspecialClienteByIdProdutoTabelaEspecial(this.produtoTabelaEspecial.idprodutotabelaespecial).subscribe(
                  produtosEspecialClientes => this.produtosEspecialClientes = produtosEspecialClientes,
                  error => { console.log("Erro: " + error) },
                  () => {
                    /* Produto Especial (Vendedores) */

                    this.produtoService.getProdutoEspecialVendedorByIdProdutoTabelaEspecial(this.produtoTabelaEspecial.idprodutotabelaespecial).subscribe(
                      produtosEspecialVendedores => this.produtosEspecialVendedores = produtosEspecialVendedores,
                      error => { console.log("Erro: " + error) },
                      () => {
                        return resolver();
                      }
                    );
                  }
                );
              }
            );
          } else {
            this.toastr.error("", "Promoção inválida!");

            this.router.navigate(["/produtos"]);
          }
        }
      );
    });

    return retorno;
  }

  prepararEnviarDados() {
    if (this.produtoTabelaEspecial != null) {
      /* Alterando */

      this.produtoTabelaEspecial.descricao = String(this.formProdutoTabelaEspecial.controls["nome"].value).toUpperCase();
      this.produtoTabelaEspecial.guidclientenapis = this.guidclientenapis;
      this.produtoTabelaEspecial.ativo = this.produtoTabelaEspecialAtivo;
      this.produtoTabelaEspecial.dt_inicio = this.formProdutoTabelaEspecial.get('dt_inicio').value;
      this.produtoTabelaEspecial.dt_fim = this.formProdutoTabelaEspecial.get('dt_fim').value;
      this.produtoTabelaEspecial.flag_naoaplicafatorpontuacaofin = this.naoAplicaFatorPontuacaoFinGlobal;
    } else {
      /* Adicionando */

      this.produtoTabelaEspecial = new ProdutoTabelaEspecial;

      this.produtoTabelaEspecial.descricao = String(this.formProdutoTabelaEspecial.controls["nome"].value).toUpperCase();
      this.produtoTabelaEspecial.guidclientenapis = this.guidclientenapis;
      this.produtoTabelaEspecial.ativo = this.produtoTabelaEspecialAtivo;
      this.produtoTabelaEspecial.dt_inicio = this.formProdutoTabelaEspecial.get('dt_inicio').value;
      this.produtoTabelaEspecial.dt_fim = this.formProdutoTabelaEspecial.get('dt_fim').value;
      this.produtoTabelaEspecial.flag_naoaplicafatorpontuacaofin = this.naoAplicaFatorPontuacaoFinGlobal;
    }

    if (this.regraPrazoHabilitada) this.produtoTabelaEspecial.id_prazostr = this.prazosVinculados.length ? this.prazosVinculados.join(';') : null;

    // Converter tipo_calculo (UI) → tipo (DB) e percentual (UI) → tipo_percentual (DB) antes de enviar
    this.produtosTabelaEspecialItens.forEach((item) => {
      if (item.tipo_calculo === 'margem') {
        item.tipo = 'M';
        item.tipo_calculo = 'M';
      } else if (item.tipo_calculo === 'desconto') {
        item.tipo = 'D';
        item.tipo_calculo = 'D';
      }
      // Sincronizar percentual (campo da tabela editável) → tipo_percentual (campo do banco)
      if (item.percentual != null) {
        item.tipo_percentual = item.percentual;
      }
    });
  }

  salvarProdutoEspecialCliente() {
    if (this.clienteSelecionado != null) {
      if (this.produtoTabelaEspecial != null) {
        let produtoEspecialCliente = new ProdutoEspecialCliente();

        produtoEspecialCliente.idprodutotabelaespecial = this.produtoTabelaEspecial.idprodutotabelaespecial;
        produtoEspecialCliente.idcliente = this.clienteSelecionado.id;
        produtoEspecialCliente.name = this.clienteSelecionado.name;

        let adicionarProdutoEspecialCliente: number = 0;

        this.produtoService.addProdutoEspecialCliente(produtoEspecialCliente).subscribe(
          adicionarProdutoEspecialClientes => adicionarProdutoEspecialCliente = adicionarProdutoEspecialClientes,
          error => { console.log("Erro: " + error) },
          () => {
            if (adicionarProdutoEspecialCliente > 0) {
              produtoEspecialCliente.idprodutoespecialcliente = adicionarProdutoEspecialCliente;

              this.produtosEspecialClientes.push(produtoEspecialCliente);

              this.produtosEspecialClientes = this.produtosEspecialClientes.sort((a, b) => {
                const compare = (v1, v2) => v1 == null ? -1 : v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

                return compare(a["name"], b["name"]);
              });

              this.clienteSelecionado = null;

              this.alterarFiltroCliente(null);

              this.clienteInvalido = false;
              this.filtrandoCliente = false;

              this.toastr.success("", "Cliente adicionado com sucesso!");
            }
          }
        );
      }
    } else {
      this.clienteInvalido = true;
    }
  }

  salvarProdutoEspecialVendedor() {
    if (this.vendedorSelecionado != null) {
      if (this.produtoTabelaEspecial != null) {
        let produtoEspecialVendedor = new ProdutoEspecialVendedor();

        produtoEspecialVendedor.idprodutotabelaespecial = this.produtoTabelaEspecial.idprodutotabelaespecial;
        produtoEspecialVendedor.idvendedor = this.vendedorSelecionado.id;
        produtoEspecialVendedor.fullname = this.vendedorSelecionado.fullname;

        let adicionarProdutoEspecialVendedor: number = 0;

        this.produtoService.addProdutoEspecialVendedor(produtoEspecialVendedor).subscribe(
          adicionarProdutoEspecialVendedores => adicionarProdutoEspecialVendedor = adicionarProdutoEspecialVendedores,
          error => { console.log("Erro: " + error) },
          () => {
            if (adicionarProdutoEspecialVendedor > 0) {
              produtoEspecialVendedor.idprodutoespecialvendedor = adicionarProdutoEspecialVendedor;

              this.produtosEspecialVendedores.push(produtoEspecialVendedor);

              this.produtosEspecialVendedores = this.produtosEspecialVendedores.sort((a, b) => {
                const compare = (v1, v2) => v1 == null ? -1 : v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

                return compare(a["fullname"], b["fullname"]);
              });

              this.vendedorSelecionado = null;

              this.alterarFiltroVendedorTipo(null);

              this.vendedorInvalido = false;
              this.filtrandoVendedor = false;

              this.toastr.success("", "Vendedor(a) adicionado com sucesso!");
            }
          }
        );
      }
    } else {
      this.vendedorInvalido = true;
    }
  }

  verificarAdicionandoAlterando() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => {
      return false;
    }

    let id: string = null;

    this.route.params.subscribe(params => { id = params.id; });

    if (id != null && !isNaN(parseInt(id)) && isFinite(parseInt(id))) {
      /* Alterando */

      const prepararReceberDados = this.prepararReceberDados(parseInt(id));

      prepararReceberDados.then(() => {
        this.carregando = false;
      });
    } else {
      /* Adicionado */

      this.carregando = false;
    }
  }

  /* Filtro (Clientes) */

  alterarFiltroCliente(filtro_selecionado: number) {
    this.reiniciarDadosCliente();

    let filtroAtual: string = this.formFiltroCliente.controls["filtro"].value;

    this.formFiltroCliente.reset();

    if (filtro_selecionado == null) {
      this.formFiltroCliente.controls["filtro"].setValue(filtroAtual);
    } else {
      this.formFiltroCliente.controls["filtro"].setValue(filtro_selecionado);
    }

    this.enviadoCliente = false;
  }

  adicionarFiltroCliente() {
    this.enviadoCliente = true;

    if (this.formFiltroCliente.invalid) {
      return;
    }

    const tipo = this.formFiltroCliente.get('filtro').value;
    const valor = this.formFiltroCliente.get('valor').value.trim();

    if (!tipo || !valor) {
      this.toastr.error("Tipo e valor são obrigatórios!", "Erro");
      return;
    }

    // Evitar duplicatas
    const jaExiste = this.filtrosAtivosClientes.some(f => f.tipo === tipo && f.valor.toLowerCase() === valor.toLowerCase());

    if (jaExiste) {
      this.toastr.warning("Este filtro já foi adicionado!", "Aviso");
      return;
    }

    this.filtrosAtivosClientes.push({ tipo, valor });

    // Limpar campo de valor
    this.formFiltroCliente.get('valor').reset();
    this.enviadoCliente = false;

    this.toastr.success("Filtro adicionado!", "Sucesso");
  }

  removerFiltroCliente(index: number) {
    this.filtrosAtivosClientes.splice(index, 1);

    // Se não houver mais filtros, voltar ao estado inicial
    if (this.filtrosAtivosClientes.length === 0) {
      this.filtrandoCliente = false;
      this.reiniciarDadosCliente();
    }
  }

  limparTodosFiltrosClientes() {
    this.filtrosAtivosClientes = [];
    this.formFiltroCliente.reset({ filtro: 'codigo', valor: '' });
    this.filtrandoCliente = false;
    this.reiniciarDadosCliente();
    this.toastr.info("Filtros removidos!", "Informação");
  }

  filtrarCliente() {
    if (this.filtrosAtivosClientes.length === 0) {
      this.toastr.info("Adicione pelo menos um filtro!", "Informação");
      return;
    }
    this.filtrandoCliente = true;
    this.clienteService.searchPaged(this.filtrosAtivosClientes, 0, 0).subscribe(resultado => {
      this.clientesOriginal = resultado.items;
      this.clientes = resultado.items;
      this.filtrandoCliente = true;
    });
  }

  aplicarFiltrosClientes() {
    if (this.filtrosAtivosClientes.length === 0) {
      this.toastr.info("Adicione pelo menos um filtro!", "Informação");
      return;
    }

    this.reiniciarDadosCliente();

    // Agrupar filtros por tipo
    const filtrosPorTipo: { [key: string]: string[] } = {};
    for (const filtro of this.filtrosAtivosClientes) {
      if (!filtrosPorTipo[filtro.tipo]) {
        filtrosPorTipo[filtro.tipo] = [];
      }
      filtrosPorTipo[filtro.tipo].push(filtro.valor.trim().toLowerCase());
    }

    // Aplicar filtros com lógica AND entre tipos e OR dentro do mesmo tipo
    this.clientes = this.clientes.filter(cliente => {
      for (const tipo in filtrosPorTipo) {
        const valoresFiltro = filtrosPorTipo[tipo];
        let atendeFiltro = false;

        switch (tipo) {
          case 'codigo':
            atendeFiltro = valoresFiltro.some(valor =>
              cliente.codigo_no_erp_do_cliente != null &&
              cliente.codigo_no_erp_do_cliente.toString().toLowerCase() === valor
            );
            break;

          case 'cnpj_cpf':
            const docClienteLimpo = cliente.client_document != null ?
              cliente.client_document.replace(/\D/g, '') : '';
            atendeFiltro = valoresFiltro.some(valor => {
              const docFiltroLimpo = valor.replace(/\D/g, '');
              return docClienteLimpo && docClienteLimpo.includes(docFiltroLimpo);
            });
            break;

          case 'nome_fantasia':
            atendeFiltro = valoresFiltro.some(valor =>
              cliente.fantasia != null && cliente.fantasia.toLowerCase().includes(valor)
            );
            break;

          case 'razao_social':
            atendeFiltro = valoresFiltro.some(valor =>
              cliente.name != null && cliente.name.toLowerCase().includes(valor)
            );
            break;

          case 'responsavel':
            atendeFiltro = valoresFiltro.some(valor =>
              cliente.name != null && cliente.name.toLowerCase().includes(valor)
            );
            break;

          case 'segment':
            atendeFiltro = valoresFiltro.some(valor =>
              cliente.segment != null && cliente.segment.toLowerCase().includes(valor)
            );
            break;

          case 'ramo_atividade':
            atendeFiltro = valoresFiltro.some(valor =>
              cliente.ramo_atividade != null && cliente.ramo_atividade.toLowerCase().includes(valor)
            );
            break;

          case 'grupo_regiao':
            atendeFiltro = valoresFiltro.some(valor =>
              cliente.grupo_regiao != null && cliente.grupo_regiao.toLowerCase().includes(valor)
            );
            break;

          case 'subsegmento':
            atendeFiltro = valoresFiltro.some(valor =>
              cliente.subsegmento != null && cliente.subsegmento.toLowerCase().includes(valor)
            );
            break;

          case 'subsegmento2':
            atendeFiltro = valoresFiltro.some(valor =>
              cliente.subsegmento2 != null && cliente.subsegmento2.toLowerCase().includes(valor)
            );
            break;

          case 'cidade':
            atendeFiltro = valoresFiltro.some(valor =>
              cliente.cidade != null && cliente.cidade.toLowerCase().includes(valor)
            );
            break;

          case 'classificacao':
            atendeFiltro = valoresFiltro.some(valor =>
              cliente.classificacao != null && cliente.classificacao.toLowerCase().includes(valor)
            );
            break;

          default:
            atendeFiltro = true;
        }

        // Se não atender a um dos filtros (AND), rejeita o cliente
        if (!atendeFiltro) {
          return false;
        }
      }
      return true;
    });

    this.filtrandoCliente = true;
    this.toastr.success(`${this.filtrosAtivosClientes.length} filtro(s) aplicado(s)!`, "Sucesso");
  }

  obterTextoFiltroCliente(tipo: string): string {
    const filtro = this.filtroClientes.find(f => f.valor === tipo);
    return filtro ? filtro.texto : tipo;
  }

  get opcoesValorFiltroCliente(): string[] {
    const campo = this.formFiltroCliente && this.formFiltroCliente.controls['filtro']
      ? this.formFiltroCliente.controls['filtro'].value
      : null;
    if (!campo) return [];
    return this.opcoesPorCampoCliente[campo] || [];
  }

  get filtroValorClienteEhSelect(): boolean {
    return this.opcoesValorFiltroCliente.length > 0;
  }

  alterarFiltroProdutoCliente(filtro_selecionado: string) {
    let filtroAtual: string = this.formFiltroCliente.controls["filtro"].value;

    this.formFiltroCliente.reset();

    if (filtro_selecionado == null) {
      this.formFiltroCliente.controls["filtro"].setValue(filtroAtual);
    } else {
      this.formFiltroCliente.controls["filtro"].setValue(filtro_selecionado);
    }

    this.enviadoCliente = false;
  }

  reiniciarDadosCliente() {
    this.clientes = JSON.parse(JSON.stringify(this.clientesOriginal));
  }

  selecionarClienteEmMassa(id_cliente: number) {
    if (this.clientesSelecionadosEmMassa.has(id_cliente)) {
      this.clientesSelecionadosEmMassa.delete(id_cliente);
    } else {
      this.clientesSelecionadosEmMassa.add(id_cliente);
    }
  }

  selecionarTodosClientes() {
    if (this.clientesSelecionadosEmMassa.size === this.clientes.length && this.clientes.length > 0) {
      this.clientesSelecionadosEmMassa.clear();
    } else {
      this.clientes.forEach(cliente => {
        if (cliente.id != null) {
          this.clientesSelecionadosEmMassa.add(cliente.id);
        }
      });
    }
  }

  estaClienteSelecionadoEmMassa(id_cliente: number): boolean {
    return this.clientesSelecionadosEmMassa.has(id_cliente);
  }

  estaTodasClientesSelecionadas(): boolean {
    return this.clientes.length > 0 && this.clientesSelecionadosEmMassa.size === this.clientes.length;
  }

  importarClientesSelecionados() {
    if (this.clientesSelecionadosEmMassa.size === 0) {
      this.toastr.error("", "Nenhum cliente selecionado!");
      return;
    }

    let contadorAdicionados = 0;

    this.clientesSelecionadosEmMassa.forEach((id_cliente) => {
      let cliente = this.clientes.find(c => c.id === id_cliente);

      if (cliente != null) {
        let produtoEspecialCliente = new ProdutoEspecialCliente();
        produtoEspecialCliente.idcliente = cliente.id;
        produtoEspecialCliente.name = cliente.name;
        produtoEspecialCliente.idprodutotabelaespecial = this.produtoTabelaEspecial?.idprodutotabelaespecial ?? 0;

        this.produtosEspecialClientes.push(produtoEspecialCliente);
        contadorAdicionados++;
      }
    });

    this.clientesSelecionadosEmMassa.clear();
    this.toastr.success("", `${contadorAdicionados} cliente(s) adicionado(s) com sucesso!`);
  }

  baixarModeloImportacaoClientes() {
    this.excelService.exportAsExcelFile(
      [{ codigo_no_erp_do_cliente: "" }],
      "modelo_importacao_clientes_promocao"
    );
  }

  onArquivoImportacaoClientesSelecionado(event: any) {
    const arquivos: FileList = event?.target?.files;
    this.arquivoImportacaoClientes = (arquivos && arquivos.length > 0) ? arquivos[0] : null;
  }

  processarImportacaoClientesPlanilha() {
    if (this.arquivoImportacaoClientes == null) {
      this.toastr.error("", "Selecione um arquivo de planilha (.xlsx).");
      return;
    }

    this.importandoClientesPlanilha = true;

    const reader = new FileReader();

    reader.onload = () => {
      try {
        const linhasExcel = this.excelService.importExcelBinaryToJson(reader.result as string) || [];

        if (linhasExcel.length === 0) {
          this.importandoClientesPlanilha = false;
          this.toastr.warning("", "A planilha nao possui linhas para importar.");
          return;
        }

        this.clienteService.getByGuidClienteNapis().subscribe(
          (clientesBase) => {
            this.linhasImportacaoClientes = this.validarLinhasImportacaoClientes(linhasExcel, clientesBase || []);
            this.importandoClientesPlanilha = false;

            this.modalService.open(this.modalImportacaoClientesPlanilha, {
              ariaLabelledBy: "titulo-importacao-clientes-planilha",
              size: "xl",
              backdrop: "static"
            });
          },
          () => {
            this.importandoClientesPlanilha = false;
            this.toastr.error("", "Nao foi possivel consultar os clientes para validar a planilha.");
          }
        );
      } catch {
        this.importandoClientesPlanilha = false;
        this.toastr.error("", "Erro ao processar a planilha de clientes.");
      }
    };

    reader.onerror = () => {
      this.importandoClientesPlanilha = false;
      this.toastr.error("", "Nao foi possivel ler a planilha selecionada.");
    };

    reader.readAsBinaryString(this.arquivoImportacaoClientes);
  }

  get importacaoClientesLinhasValidas(): number {
    return this.linhasImportacaoClientes.filter(l => l.valido).length;
  }

  get importacaoClientesLinhasInvalidas(): number {
    return this.linhasImportacaoClientes.filter(l => !l.valido).length;
  }

  confirmarImportacaoClientesPlanilha(modal: any) {
    const validas = this.linhasImportacaoClientes.filter(l => l.valido && l.idcliente != null);

    if (validas.length === 0) {
      this.toastr.warning("", "Nenhuma linha valida para importar.");
      return;
    }

    let adicionados = 0;

    validas.forEach((linha) => {
      const clienteJaImportado = this.produtosEspecialClientes.some(c => c.idcliente === linha.idcliente);

      if (!clienteJaImportado) {
        const produtoEspecialCliente = new ProdutoEspecialCliente();
        produtoEspecialCliente.idcliente = linha.idcliente;
        produtoEspecialCliente.name = linha.nome;
        produtoEspecialCliente.idprodutotabelaespecial = this.produtoTabelaEspecial?.idprodutotabelaespecial ?? 0;

        this.produtosEspecialClientes.push(produtoEspecialCliente);
        adicionados++;
      }
    });

    this.produtosEspecialClientes.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    this.linhasImportacaoClientes = [];
    this.arquivoImportacaoClientes = null;

    if (modal) {
      modal.close();
    }

    this.toastr.success("", `${adicionados} cliente(s) importado(s) da planilha!`);
  }

  private validarLinhasImportacaoClientes(linhasExcel: any[], clientesBase: Cliente[]): ImportacaoPromocaoClienteLinha[] {
    const clientesPorCodigo = new Map<string, Cliente>();

    clientesBase.forEach(cliente => {
      const codigo = this.normalizarCodigoImportacaoCliente(cliente.codigo_no_erp_do_cliente);

      if (codigo !== "") {
        clientesPorCodigo.set(codigo, cliente);
      }
    });

    const codigosDaPlanilha = new Set<string>();

    return linhasExcel.map((linhaExcel, index) => {
      const codigo = this.obterCodigoClienteImportacao(linhaExcel);
      const linha: ImportacaoPromocaoClienteLinha = {
        linha: index + 2,
        codigo_no_erp_do_cliente: codigo,
        idcliente: null,
        nome: "",
        valido: false,
        erro: null
      };

      if (codigo === "") {
        linha.erro = "Codigo do cliente nao informado.";
        return linha;
      }

      if (codigosDaPlanilha.has(codigo)) {
        linha.erro = "Codigo repetido na planilha.";
        return linha;
      }

      codigosDaPlanilha.add(codigo);

      const cliente = clientesPorCodigo.get(codigo);

      if (cliente == null) {
        linha.erro = "Cliente nao encontrado.";
        return linha;
      }

      if (this.produtosEspecialClientes.some(c => c.idcliente === cliente.id)) {
        linha.idcliente = cliente.id;
        linha.nome = cliente.name;
        linha.erro = "Cliente ja esta importado na promocao.";
        return linha;
      }

      linha.idcliente = cliente.id;
      linha.nome = cliente.name;
      linha.valido = true;

      return linha;
    });
  }

  private obterCodigoClienteImportacao(linhaExcel: any): string {
    const nomesColuna = [
      "codigo_no_erp_do_cliente",
      "client.codigo_no_erp_do_cliente",
      "codigo",
      "cod_cliente",
      "cliente"
    ];

    for (const nomeColuna of nomesColuna) {
      if (linhaExcel[nomeColuna] != null) {
        return this.normalizarCodigoImportacaoCliente(linhaExcel[nomeColuna]);
      }
    }

    const chave = Object.keys(linhaExcel).find(c =>
      this.normalizarNomeColunaImportacao(c) === "codigo_no_erp_do_cliente" ||
      this.normalizarNomeColunaImportacao(c) === "client_codigo_no_erp_do_cliente"
    );

    return chave != null ? this.normalizarCodigoImportacaoCliente(linhaExcel[chave]) : "";
  }

  private normalizarCodigoImportacaoCliente(valor: any): string {
    return valor == null ? "" : valor.toString().trim();
  }

  private normalizarNomeColunaImportacao(nome: string): string {
    return (nome || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[.\s-]+/g, "_")
      .toLowerCase()
      .trim();
  }

  abrirModalClientesAdicionadosEmMassa(content: any) {
    this.modalService.open(content, {
      ariaLabelledBy: "titulo-clientes-adicionados-em-massa",
      size: "lg",
      backdrop: "static"
    });
  }

  selecionarClienteFiltrado(cliente_selecionado: string) {
    if (cliente_selecionado != null && cliente_selecionado != "") {
      let clienteSelecionado: Cliente = this.clientes.find(clientes => clientes.codigo_no_erp_do_cliente == cliente_selecionado);

      if (clienteSelecionado != null) {
        /* Inadimplente */

        if (clienteSelecionado.status_financeiro != null) {
          this.toastr.warning("", "Cliente c/ inadimplência!");
        }

        let produtoEspecialCliente = this.produtosEspecialClientes.find(produtoEspecialCliente => produtoEspecialCliente.idcliente == clienteSelecionado.id);

        if (produtoEspecialCliente == null) {
          this.clienteSelecionado = clienteSelecionado;

          this.alterarFiltroProdutoCliente(null);

          this.clienteInvalido = false;
          this.filtrandoCliente = false;
        } else {
          this.toastr.error("", "Cliente já adicionado!");
        }
      }
    } else {
      this.toastr.error("", "Houve um erro, contate o suporte...");
    }
  }

  /* Filtro (Produtos) */

  alterarFiltroProduto(filtro_selecionado: string) {
    let filtroAtual: string = this.formFiltroProduto.controls["filtro"].value;

    this.formFiltroProduto.reset();

    if (filtro_selecionado == null) {
      this.formFiltroProduto.controls["filtro"].setValue(filtroAtual);
    } else {
      this.formFiltroProduto.controls["filtro"].setValue(filtro_selecionado);
    }

    this.organizarFiltroProduto();

    this.enviadoProduto = false;
  }

  adicionarFiltro() {
    this.enviadoProduto = true;

    if (this.formFiltroProduto.invalid) {
      return;
    }

    const tipo = this.formFiltroProduto.get('filtro').value;
    const valor = this.formFiltroProduto.get('valor').value.trim();

    if (!tipo || !valor) {
      this.toastr.error("Tipo e valor são obrigatórios!", "Erro");
      return;
    }

    // Evitar duplicatas
    const jaExiste = this.filtrosAtivos.some(f => f.tipo === tipo && f.valor.toLowerCase() === valor.toLowerCase());

    if (jaExiste) {
      this.toastr.warning("Este filtro já foi adicionado!", "Aviso");
      return;
    }

    this.filtrosAtivos.push({ tipo, valor });

    // Limpar campo de valor
    this.formFiltroProduto.get('valor').reset();
    this.enviadoProduto = false;

    this.toastr.success("Filtro adicionado!", "Sucesso");
  }

  removerFiltro(index: number) {
    this.filtrosAtivos.splice(index, 1);

    // Se não houver mais filtros, voltar ao estado inicial
    if (this.filtrosAtivos.length === 0) {
      this.filtrandoProduto = false;
      this.reiniciarDadosProduto();
    }
  }

  aplicarFiltros() {
    if (this.filtrosAtivos.length === 0) {
      this.toastr.info("Adicione pelo menos um filtro!", "Informação");
      return;
    }

    this.reiniciarDadosProduto();

    // Agrupar filtros por tipo
    const filtrosPorTipo: { [key: string]: string[] } = {};
    for (const filtro of this.filtrosAtivos) {
      if (!filtrosPorTipo[filtro.tipo]) {
        filtrosPorTipo[filtro.tipo] = [];
      }
      filtrosPorTipo[filtro.tipo].push(filtro.valor.trim().toLowerCase());
    }

    // Aplicar filtros com lógica AND entre tipos e OR dentro do mesmo tipo
    this.produtos = this.produtos.filter(produto => {
      for (const tipo in filtrosPorTipo) {
        const valoresFiltro = filtrosPorTipo[tipo];
        let atendeFiltro = false;

        switch (tipo) {
          case 'codigo':
            if (produto.codigo == null) {
              return false;
            }
            atendeFiltro = valoresFiltro.some(valor =>
              produto.codigo.toLowerCase().indexOf(valor) !== -1
            );
            break;

          case 'descricao':
            if (produto.name == null) {
              return false;
            }
            atendeFiltro = valoresFiltro.some(valor =>
              produto.name.toLowerCase().indexOf(valor) !== -1
            );
            break;

          case 'marca':
            if (produto.grupo_estoque == null) {
              return false;
            }
            atendeFiltro = valoresFiltro.some(valor =>
              produto.grupo_estoque.toLowerCase().indexOf(valor) !== -1
            );
            break;

          case 'subgrupo1':
            if (produto.subgrupo_estoque == null) {
              return false;
            }
            atendeFiltro = valoresFiltro.some(valor =>
              produto.subgrupo_estoque.toLowerCase().indexOf(valor) !== -1
            );
            break;

          case 'subgrupo2':
            if (produto.subgrupo_estoque2 == null) {
              return false;
            }
            atendeFiltro = valoresFiltro.some(valor =>
              produto.subgrupo_estoque2.toLowerCase().indexOf(valor) !== -1
            );
            break;

          case 'subgrupo3':
            if (produto.subgrupo_estoque3 == null) {
              return false;
            }
            atendeFiltro = valoresFiltro.some(valor =>
              produto.subgrupo_estoque3.toLowerCase().indexOf(valor) !== -1
            );
            break;

          case 'subgrupo4':
            if (produto.subgrupo_estoque4 == null) {
              return false;
            }
            atendeFiltro = valoresFiltro.some(valor =>
              produto.subgrupo_estoque4.toLowerCase().indexOf(valor) !== -1
            );
            break;

          case 'subgrupo5':
            if (produto.subgrupo_estoque5 == null) {
              return false;
            }
            atendeFiltro = valoresFiltro.some(valor =>
              produto.subgrupo_estoque5.toLowerCase().indexOf(valor) !== -1
            );
            break;

          default:
            atendeFiltro = true;
        }

        // Se não atender a um dos filtros (AND), rejeita o produto
        if (!atendeFiltro) {
          return false;
        }
      }
      return true;
    });

    this.filtrandoProduto = true;
    this.toastr.success(`${this.filtrosAtivos.length} filtro(s) aplicado(s)!`, "Sucesso");
  }

  limparTodosFiltros() {
    this.filtrosAtivos = [];
    this.formFiltroProduto.reset({ filtro: 'descricao', valor: '' });
    this.filtrandoProduto = false;
    this.reiniciarDadosProduto();
    this.organizarFiltroProduto();
    this.toastr.info("Filtros removidos!", "Informação");
  }

  filtrarProduto() {
    if (this.filtrosAtivos.length === 0) {
      this.toastr.info("Adicione pelo menos um filtro!", "Informação");
      return;
    }
    this.filtrandoProduto = true;
    this.produtoService.searchPaged(this.filtrosAtivos, 0, 500).subscribe(resultado => {
      this.produtosOriginal = resultado.items;
      this.produtos = resultado.items;
      this.indexarProdutosPorId();
      this.filtrandoProduto = true;
    });
  }

  organizarFiltroProduto() {
    // Todos os campos estão sempre habilitados para permitir múltiplos filtros
    const campo = this.formFiltroProduto.get("valor");
    if (campo != null) {
      campo.enable();
    }
  }

  obterTextoFiltro(tipo: string): string {
    const filtro = this.filtroProdutos.find(f => f.valor === tipo);
    return filtro ? filtro.texto : tipo;
  }

  reiniciarDadosProduto() {
    this.produtos = JSON.parse(JSON.stringify(this.produtosOriginal));
  }

  extrairMarcasDisponiveis() {
    // Extrair todas as marcas (grupo_estoque) dos produtos e remover duplicatas
    const marcasSet = new Set<string>();

    this.produtosOriginal.forEach(produto => {
      if (produto.grupo_estoque) {
        marcasSet.add(produto.grupo_estoque.trim());
      }
    });

    this.marcasDisponiveis = Array.from(marcasSet).sort();
  }

  extrairSubgruposDisponiveis() {
    // Extrair subgrupos separados para cada campo
    this.subgrupos1Disponiveis = this.extrairValoresUnicos(this.produtosOriginal, 'subgrupo_estoque');
    this.subgrupos2Disponiveis = this.extrairValoresUnicos(this.produtosOriginal, 'subgrupo_estoque2');
    this.subgrupos3Disponiveis = this.extrairValoresUnicos(this.produtosOriginal, 'subgrupo_estoque3');
    this.subgrupos4Disponiveis = this.extrairValoresUnicos(this.produtosOriginal, 'subgrupo_estoque4');
    this.subgrupos5Disponiveis = this.extrairValoresUnicos(this.produtosOriginal, 'subgrupo_estoque5');
  }

  private extrairValoresUnicos(items: any[], campo: string): string[] {
    const valoresSet = new Set<string>();
    items.forEach(item => {
      const valor = item[campo];
      if (valor != null && typeof valor === 'string' && valor.trim() !== '') {
        valoresSet.add(valor.trim());
      }
    });
    return Array.from(valoresSet).sort();
  }

  extrairOpcoesClientes() {
    // As opções de segmentação vêm do endpoint GetClienteFiltrosConfigurados (opcoesPorCampoCliente).
    // Este método é mantido para compatibilidade mas não realiza mais extração local.
  }

  private indexarProdutosPorId(): void {
    this.produtosPorId.clear();
    (this.produtos || []).forEach(p => {
      if (p && p.id != null) {
        this.produtosPorId.set(p.id, p);
      }
    });
  }

  extrairEmpresasDisponiveis() {
    // Extrair todas as empresas (nm_empresa) dos vendedores
    const empresasSet = new Set<string>();

    this.vendedoresOriginal.forEach(vendedor => {
      if (vendedor.nm_empresa != null && vendedor.nm_empresa.trim() !== '') {
        empresasSet.add(vendedor.nm_empresa.trim());
      }
    });

    this.empresasDisponiveis = Array.from(empresasSet).sort();
  }

  estaProdutoSelecionadoEmMassa(id_produto: number): boolean {
    return this.produtosSelecionadosEmMassa.has(id_produto);
  }

  /* Filtro (Vendedores) */

  adicionarFiltroVendedor() {
    this.enviadoVendedor = true;

    if (this.formFiltroVendedor.invalid) {
      return;
    }

    const tipo = this.formFiltroVendedor.get('filtro').value;
    const valor = this.formFiltroVendedor.get('valor').value.trim();

    if (!tipo || !valor) {
      this.toastr.error("Tipo e valor são obrigatórios!", "Erro");
      return;
    }

    // Evitar duplicatas
    const jaExiste = this.filtrosAtivosVendedores.some(f => f.tipo === tipo && f.valor.toLowerCase() === valor.toLowerCase());

    if (jaExiste) {
      this.toastr.warning("Este filtro já foi adicionado!", "Aviso");
      return;
    }

    this.filtrosAtivosVendedores.push({ tipo, valor });

    // Limpar campo de valor
    this.formFiltroVendedor.get('valor').reset();
    this.enviadoVendedor = false;

    this.toastr.success("Filtro adicionado!", "Sucesso");
  }

  removerFiltroVendedor(index: number) {
    this.filtrosAtivosVendedores.splice(index, 1);

    // Se não houver mais filtros, voltar ao estado inicial
    if (this.filtrosAtivosVendedores.length === 0) {
      this.filtrandoVendedor = false;
      this.reiniciarDadosVendedor();
    }
  }

  limparTodosFiltrosVendedores() {
    this.filtrosAtivosVendedores = [];
    this.formFiltroVendedor.reset({ filtro: 'codigo', valor: '' });
    this.filtrandoVendedor = false;
    this.reiniciarDadosVendedor();
    this.toastr.info("Filtros removidos!", "Informação");
  }

  filtrarVendedor() {
    this.aplicarFiltrosVendedores();
  }

  aplicarFiltrosVendedores() {
    if (this.filtrosAtivosVendedores.length === 0) {
      this.toastr.info("Adicione pelo menos um filtro!", "Informação");
      return;
    }

    this.reiniciarDadosVendedor();

    // Agrupar filtros por tipo
    const filtrosPorTipo: { [key: string]: string[] } = {};
    for (const filtro of this.filtrosAtivosVendedores) {
      if (!filtrosPorTipo[filtro.tipo]) {
        filtrosPorTipo[filtro.tipo] = [];
      }
      filtrosPorTipo[filtro.tipo].push(filtro.valor.trim().toLowerCase());
    }

    // Aplicar filtros com lógica AND entre tipos e OR dentro do mesmo tipo
    this.vendedores = this.vendedores.filter(vendedor => {
      for (const tipo in filtrosPorTipo) {
        const valoresFiltro = filtrosPorTipo[tipo];
        let atendeFiltro = false;

        switch (tipo) {
          case 'codigo':
            atendeFiltro = valoresFiltro.some(valor =>
              vendedor.codigo_no_erp_do_cliente != null &&
              vendedor.codigo_no_erp_do_cliente.toString().toLowerCase() === valor
            );
            break;

          case 'nome':
            atendeFiltro = valoresFiltro.some(valor =>
              vendedor.fullname != null && vendedor.fullname.toLowerCase().includes(valor)
            );
            break;

          case 'empresa':
            atendeFiltro = valoresFiltro.some(valor =>
              vendedor.nm_empresa != null && vendedor.nm_empresa.toLowerCase().includes(valor)
            );
            break;

          default:
            atendeFiltro = true;
        }

        // Se não atender a um dos filtros (AND), rejeita o vendedor
        if (!atendeFiltro) {
          return false;
        }
      }
      return true;
    });

    this.filtrandoVendedor = true;
    this.toastr.success(`${this.filtrosAtivosVendedores.length} filtro(s) aplicado(s)!`, "Sucesso");
  }

  obterTextoFiltroVendedor(tipo: string): string {
    const filtro = this.filtroVendedores.find(f => f.valor === tipo);
    return filtro ? filtro.texto : tipo;
  }

  alterarFiltroVendedorTipo(filtro_selecionado: string) {
    let filtroAtual: string = this.formFiltroVendedor.controls["filtro"].value;

    this.formFiltroVendedor.reset();

    if (filtro_selecionado == null) {
      this.formFiltroVendedor.controls["filtro"].setValue(filtroAtual);
    } else {
      this.formFiltroVendedor.controls["filtro"].setValue(filtro_selecionado);
    }

    this.enviadoVendedor = false;
  }

  reiniciarDadosVendedor() {
    this.vendedores = JSON.parse(JSON.stringify(this.vendedoresOriginal));
  }

  selecionarVendedorEmMassa(id_vendedor: number) {
    if (this.vendedoresSelecionadosEmMassa.has(id_vendedor)) {
      this.vendedoresSelecionadosEmMassa.delete(id_vendedor);
    } else {
      this.vendedoresSelecionadosEmMassa.add(id_vendedor);
    }
  }

  selecionarTodosVendedores() {
    if (this.vendedoresSelecionadosEmMassa.size === this.vendedores.length && this.vendedores.length > 0) {
      this.vendedoresSelecionadosEmMassa.clear();
    } else {
      this.vendedores.forEach(vendedor => {
        if (vendedor.id != null) {
          this.vendedoresSelecionadosEmMassa.add(vendedor.id);
        }
      });
    }
  }

  estaVendedorSelecionadoEmMassa(id_vendedor: number): boolean {
    return this.vendedoresSelecionadosEmMassa.has(id_vendedor);
  }

  estaTodasVendedoresSelecionadas(): boolean {
    return this.vendedores.length > 0 && this.vendedoresSelecionadosEmMassa.size === this.vendedores.length;
  }

  importarVendedoresSelecionados() {
    if (this.vendedoresSelecionadosEmMassa.size === 0) {
      this.toastr.error("", "Nenhum vendedor(a) selecionado(a)!");
      return;
    }

    let contadorAdicionados = 0;

    this.vendedoresSelecionadosEmMassa.forEach((id_vendedor) => {
      let vendedor = this.vendedores.find(v => v.id === id_vendedor);

      if (vendedor != null) {
        let produtoEspecialVendedor = new ProdutoEspecialVendedor();
        produtoEspecialVendedor.idvendedor = vendedor.id;
        produtoEspecialVendedor.fullname = vendedor.fullname;
        produtoEspecialVendedor.idprodutotabelaespecial = this.produtoTabelaEspecial?.idprodutotabelaespecial ?? 0;

        this.produtosEspecialVendedores.push(produtoEspecialVendedor);
        contadorAdicionados++;
      }
    });

    this.vendedoresSelecionadosEmMassa.clear();
    this.toastr.success("", `${contadorAdicionados} vendedor(a)(s) adicionado(a)(s) com sucesso!`);
  }

  abrirModalVendedoresAdicionadosEmMassa(content: any) {
    this.modalService.open(content, {
      ariaLabelledBy: "titulo-vendedores-adicionados-em-massa",
      size: "lg",
      backdrop: "static"
    });
  }

  selecionarVendedorFiltrado(id_vendedor: number) {
    let vendedorSelecionado = this.vendedores.find(vendedor => vendedor.id == id_vendedor);

    if (vendedorSelecionado != null) {
      let produtoEspecialVendedor = this.produtosEspecialVendedores.find(produtoEspecialVendedor => produtoEspecialVendedor.idvendedor == vendedorSelecionado.id);

      if (produtoEspecialVendedor == null) {
        this.vendedorSelecionado = vendedorSelecionado;

        this.alterarFiltroVendedorTipo(null);

        this.vendedorInvalido = false;
        this.filtrandoVendedor = false;
      } else {
        this.toastr.error("", "Vendedor(a) já adicionado(a)!");
      }
    }
  }

  /* Itens (Menu) */

  get regrasGrupoClientes(): PromoRegraGrupo[] {
    return this.regrasGrupo.filter(r => r.entidade === 'cliente');
  }

  get regrasGrupoProdutos(): PromoRegraGrupo[] {
    return this.regrasGrupo.filter(r => r.entidade === 'produto');
  }

  alterarItemSelecionado(item: number) {
    if (item === 5 && !this.regraPrazoHabilitada) return;
    this.validarItens(item);

    if (item > this.itemMaximoPermitido) {
      return;
    }

    this.itemSelecionado = item;

    if (item === 4) {
      this.carregarRegrasGrupo();
    }
  }

  prosseguirItemSelecionado(item: number) {
    this.validarItens(item);

    if ((item + 1) > this.itemMaximoPermitido) {
      return;
    }

    this.itemSelecionado = item + 1;
  }

  retornarItemSelecionado(item: number) {
    if (item === 5) {
      this.alterarItemSelecionado(4);
      return;
    }
    this.itemSelecionado = item - 1;
  }

  validarItens(item: number) {
    if (this.formProdutoTabelaEspecial.invalid) {
      this.enviadoProdutoTabelaEspecial = true;

      this.toastr.error("", "Informações inválidas!");

      this.itemSelecionado = 0;
      this.itemMaximoPermitido = 0;

      return;
    }

    this.itemMaximoPermitido = this.regraPrazoHabilitada ? 5 : 4;
  }

  /* Lotes */

  removerLoteProduto() {
    if (this.produtoSelecionado != null) {
      /* Lote */

      this.produtoSelecionado.id_lote = null;
      this.produtoSelecionado.nome_lote = null;
      this.produtoSelecionado.cod_lote = null;
      this.produtoSelecionado.estoque_lote = null;
      this.produtoSelecionado.validade_lote = null;
      this.produtoSelecionado.preco_lote = null;
    }
  }

  selecionarLoteProduto(id_lote: number) {
    if (this.produtoSelecionado != null) {
      let produtoLote = this.produtosLotes.find(produtoLote => produtoLote.id == id_lote);

      if (produtoLote != null) {
        this.produtoSelecionado.valor = produtoLote.preco;

        /* Lote */

        this.produtoSelecionado.id_lote = produtoLote.id;
        this.produtoSelecionado.nome_lote = produtoLote.nomelote;
        this.produtoSelecionado.cod_lote = produtoLote.codlote;
        this.produtoSelecionado.estoque_lote = produtoLote.estoque;
        this.produtoSelecionado.validade_lote = produtoLote.validade;
        this.produtoSelecionado.preco_lote = produtoLote.preco;

        this.produtoLoteErro = false;

        /* Formulário (Tabela Especial (Itens)) */

        this.formProdutoTabelaEspecialItem.controls["qtde"].enable();
        this.formProdutoTabelaEspecialItem.controls["preco"].enable();
      }
    }

    this.modalReferenciaSelecionarLote.close();
  }

  verificarExibirLote() {
    this.exibirLote = false;

    this.produtosTabelaEspecialItens.forEach((item) => {
      if (item.id_lote != null) {
        this.exibirLote = true;
      }
    });
  }

  /* Produto (Qtde) */

  decrementarQtdeProduto() {
    if (this.produtoSelecionado != null) {
      if (this.produtoSelecionado.qtde_lote == 0 || (this.produtoSelecionado.qtde_lote > 0 && this.produtoSelecionado.id_lote != null)) {
        let qtde: number = this.formProdutoTabelaEspecialItem.controls["qtde"].value;

        if (this.produtoSelecionado.multiplo != null && this.produtoSelecionado.multiplo != 0 && this.produtoSelecionado.multiplo != 1) {
          /* C/ Múltiplo */

          if (qtde <= 1) {
            qtde = null;
          } else {
            let qtdeFutura: number = qtde - this.produtoSelecionado.multiplo;

            if (qtdeFutura == 0 || qtdeFutura % this.produtoSelecionado.multiplo != 0) {
              qtde = null;
            } else {
              qtde = qtde - this.produtoSelecionado.multiplo;
            }
          }
        } else {
          /* S/ Múltiplo */

          if (qtde <= 1) {
            qtde = null;
          } else {
            qtde--;
          }
        }

        this.formProdutoTabelaEspecialItem.controls["qtde"].setValue(qtde);
      }
    }
  }

  incrementarQtdeProduto(id_produto: number) {
    if (this.produtoSelecionado != null) {
      if (this.produtoSelecionado.qtde_lote == 0 || (this.produtoSelecionado.qtde_lote > 0 && this.produtoSelecionado.id_lote != null)) {
        let qtde: number = this.formProdutoTabelaEspecialItem.controls["qtde"].value;

        if (this.produtoSelecionado.multiplo != null && this.produtoSelecionado.multiplo != 0 && this.produtoSelecionado.multiplo != 1) {
          /* C/ Múltiplo */

          if (qtde <= 0 || qtde == null) {
            qtde = this.produtoSelecionado.multiplo;
          } else {
            let qtdeFutura: number = qtde + this.produtoSelecionado.multiplo;

            if (qtdeFutura % this.produtoSelecionado.multiplo != 0) {
              qtde = this.produtoSelecionado.multiplo;
            } else {
              qtde = qtde + this.produtoSelecionado.multiplo;
            }
          }
        } else {
          /* S/ Múltiplo */

          if (qtde <= 0 || qtde == null) {
            qtde = 1;
          } else {
            qtde++;
          }
        }

        this.formProdutoTabelaEspecialItem.controls["qtde"].setValue(qtde);
      }
    }
  }

  /* Produto (Tabela Especial (Itens)) */

  selecionarProdutoTabelaEspecialItemAtivo(ativo: string) {
    this.produtoTabelaEspecialItemAtivo = ativo == "0" ? false : true;
  }

  /* Produto (Tabela Especial) */

  selecionarProdutoTabelaEspecialAtivo(ativo: string) {
    this.produtoTabelaEspecialAtivo = ativo == "0" ? false : true;
  }

  /* Verificação (Qtde) */

  verificarProdutoQtde() {
    if (this.produtoSelecionado != null) {
      let qtde: number = this.formProdutoTabelaEspecialItem.controls["qtde"].value;

      if (qtde != 0 && qtde != null) {
        if (this.produtoSelecionado.multiplo != null && this.produtoSelecionado.multiplo != 0) {
          if (qtde % this.produtoSelecionado.multiplo != 0) {
            this.toastr.error("", "Qtde somente múltipla de " + this.produtoSelecionado.multiplo + "...");

            this.formProdutoTabelaEspecialItem.controls["qtde"].setErrors({ "invalid": true });
          }
        }
      }
    }
  }

  /* Modal */

  modalSelecionarLote(content: any, id_produto: number) {
    if (this.produtoSelecionado != null) {
      this.produtoService.getProdutoLoteByIdProduto(id_produto, this.obs_gerais, null, null).subscribe(
        produtosLotes => this.produtosLotes = produtosLotes,
        error => { console.log("Erro: " + error) },
        () => {
          this.modalReferenciaSelecionarLote = this.modalService.open(content, {
            ariaLabelledBy: "titulo-selecionar-lote",
            size: "lg",
            backdrop: "static"
          });
        }
      );
    }
  }

  modalNotasFiscaisSinteticas(content: any, client_document: string) {
    client_document = client_document.replace(/[.]/g, "").replace(/[/]/g, "").replace(/[-]/g, "");

    this.notaFiscalService.getNotaFiscalSinteticaByCNPJ(client_document).subscribe(
      notaFiscal => this.notasFiscais = notaFiscal,
      error => { console.log("Erro: " + error) },
      () => {
        if (this.notasFiscais.length > 0) {
          this.modalService.open(content, {
            ariaLabelledBy: "titulo-notas-fiscais-sinteticas",
            size: "lg",
            backdrop: "static"
          });
        } else {
          this.toastr.error("", "Nenhuma nota fiscal encontrada!");
        }
      }
    );
  }

  /* Importação em Massa (Produtos) */

  selecionarProdutoEmMassa(id_produto: number) {
    if (this.produtosSelecionadosEmMassa.has(id_produto)) {
      this.produtosSelecionadosEmMassa.delete(id_produto);
    } else {
      this.produtosSelecionadosEmMassa.add(id_produto);
    }
  }

  selecionarTodosProdutos() {
    if (this.produtosSelecionadosEmMassa.size === this.produtos.length && this.produtos.length > 0) {
      this.produtosSelecionadosEmMassa.clear();
    } else {
      this.produtos.forEach(produto => {
        if (produto.id != null) {
          this.produtosSelecionadosEmMassa.add(produto.id);
        }
      });
    }
  }

  estaTodasSelecionadas(): boolean {
    return this.produtos.length > 0 && this.produtosSelecionadosEmMassa.size === this.produtos.length;
  }

  private sincronizarDatasFormComItensSeVazio(): void {
    if (!this.formProdutoTabelaEspecial) return;

    const dtInicioControl = this.formProdutoTabelaEspecial.get('dt_inicio');
    const dtFimControl = this.formProdutoTabelaEspecial.get('dt_fim');

    if (!dtInicioControl || !dtFimControl) return;

    const dtInicioAtual = dtInicioControl.value;
    const dtFimAtual = dtFimControl.value;

    // Só preencher automaticamente se o formulário ainda estiver vazio
    const formVazio = !dtInicioAtual || !dtFimAtual;
    if (!formVazio) return;

    if (!this.produtosTabelaEspecialItens || this.produtosTabelaEspecialItens.length === 0) return;

    // Encontrar o primeiro item com dt_inicio e dt_fim válidos
    const itemComDatas = this.produtosTabelaEspecialItens.find(i => i && i.dt_inicio && i.dt_fim);
    if (!itemComDatas) return;

    // Setar no form
    dtInicioControl.setValue(this.normalizarDataParaInput(itemComDatas.dt_inicio));
    dtFimControl.setValue(this.normalizarDataParaInput(itemComDatas.dt_fim));
  }

  private normalizarDataParaInput(data: any): string {
    if (!data) return '';

    // Se já for string no formato YYYY-MM-DD, retornar como está
    if (typeof data === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(data)) {
      return data;
    }

    // Se for objeto Date
    if (data instanceof Date) {
      const year = data.getFullYear();
      const month = String(data.getMonth() + 1).padStart(2, '0');
      const day = String(data.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    // Se for string em outro formato, tentar converter
    if (typeof data === 'string') {
      const date = new Date(data);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }

    return '';
  }

  importarProdutosSelecionados() {
    if (this.produtosSelecionadosEmMassa.size === 0) {
      this.toastr.error("", "Nenhum produto selecionado!");
      return;
    }

    if (this.percentualProdutos === 0 || this.percentualProdutos === null) {
      this.toastr.error("", "Informe o percentual!");
      return;
    }

    let contadorAdicionados = 0;

    this.produtosSelecionadosEmMassa.forEach((id_produto) => {
      // Buscar o produto na lista filtrada (produtos) para obter o preço base correto
      let produto = this.produtos.find(p => p.id === id_produto);

      if (produto != null) {
        // Usar produto.price (desconto) e produto.custo (custo) para calcular o preço final
        let precoBase = produto.price > 0 ? produto.price : produto.custo;
        let preco_especial: number;

        if (this.tipoCalculo === 'margem') {
          // Margem: preço_final = preço_base * (1 + margem/100)
          preco_especial = precoBase * (1 + (this.percentualProdutos / 100));
        } else {
          // Desconto: preço_final = preço_base * (1 - desconto/100)
          preco_especial = precoBase * (1 - (this.percentualProdutos / 100));
        }

        let produtoTabelaEspecialItens = new ProdutoTabelaEspecialItens();

        produtoTabelaEspecialItens.ativo = true;
        produtoTabelaEspecialItens.preco = Math.round(preco_especial * 100) / 100;
        produtoTabelaEspecialItens.guidclientenapis = this.guidclientenapis;
        produtoTabelaEspecialItens.codigo = produto.codigo;
        produtoTabelaEspecialItens.qtd_minima = this.quantidadeMinimaProdutos;
        produtoTabelaEspecialItens.id_lote = null;

        // price = preço base (pode ser price ou custo)
        // custo armazenado para referência
        produtoTabelaEspecialItens.name = produto.name;
        produtoTabelaEspecialItens.price = precoBase;
        produtoTabelaEspecialItens.custo = produto.custo;

        // Armazenar percentual e tipo de cálculo para envio ao banco
        produtoTabelaEspecialItens.percentual = this.percentualProdutos;
        produtoTabelaEspecialItens.tipo_calculo = this.tipoCalculo;

        this.produtosTabelaEspecialItens.push(produtoTabelaEspecialItens);
        contadorAdicionados++;
      }
    });

    this.organizarProdutoTabelaEspecialItem();
    this.sincronizarDatasFormComItensSeVazio();
    this.produtosSelecionadosEmMassa.clear();

    this.toastr.success("", `${contadorAdicionados} produto(s) adicionado(s) com sucesso!`);
  }

  /* ===================== Importação via planilha ===================== */

  /** Baixa a planilha-modelo de importação. */
  baixarModeloImportacao() {
    this.promocoesService.downloadTemplatePromocao().subscribe(
      (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'modelo_importacao_promocoes.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      () => this.toastr.error('', 'Não foi possível baixar o modelo.')
    );
  }

  /** Captura o arquivo selecionado no input. */
  onArquivoImportacaoSelecionado(event: any) {
    const arquivos: FileList = event?.target?.files;
    this.arquivoImportacao = (arquivos && arquivos.length > 0) ? arquivos[0] : null;
  }

  /** Envia a planilha ao backend, recebe o preview e abre o modal de conferência. */
  processarImportacaoPlanilha() {
    if (this.arquivoImportacao == null) {
      this.toastr.error('', 'Selecione um arquivo de planilha (.xlsx).');
      return;
    }

    this.importandoPlanilha = true;

    this.promocoesService.importarPlanilhaPromocao(this.arquivoImportacao).subscribe(
      (linhas) => {
        this.importandoPlanilha = false;
        this.linhasImportacao = linhas || [];

        if (this.linhasImportacao.length === 0) {
          this.toastr.warning('', 'A planilha não retornou nenhuma linha.');
          return;
        }

        this.modalService.open(this.modalImportacaoPlanilha, {
          ariaLabelledBy: 'titulo-importacao-planilha',
          size: 'xl',
          backdrop: 'static'
        });
      },
      (erro) => {
        this.importandoPlanilha = false;
        const msg = erro?.error?.message || 'Erro ao processar a planilha.';
        this.toastr.error('', msg);
      }
    );
  }

  get importacaoLinhasValidas(): number {
    return this.linhasImportacao.filter(l => l.valido).length;
  }

  get importacaoLinhasInvalidas(): number {
    return this.linhasImportacao.filter(l => !l.valido).length;
  }

  /** Converte as linhas válidas em itens da tabela especial e adiciona à lista editável. */
  confirmarImportacaoPlanilha(modal: any) {
    const validas = this.linhasImportacao.filter(l => l.valido);

    if (validas.length === 0) {
      this.toastr.warning('', 'Nenhuma linha válida para importar.');
      return;
    }

    let adicionados = 0;

    validas.forEach((linha) => {
      const item = new ProdutoTabelaEspecialItens();

      item.ativo = linha.ativo;
      item.preco = linha.preco;
      item.guidclientenapis = this.guidclientenapis;
      item.codigo = linha.codigo;
      item.qtd_minima = linha.qtd_minima;
      item.id_lote = null;
      item.name = linha.nome;
      item.price = linha.preco_base;
      item.custo = linha.custo;
      item.percentual = linha.percentual;
      item.tipo_calculo = linha.tipo_calculo;
      item.cota_total = linha.cota_total;
      item.dt_inicio = linha.dt_inicio;
      item.dt_fim = linha.dt_fim;

      this.produtosTabelaEspecialItens.push(item);
      adicionados++;
    });

    this.organizarProdutoTabelaEspecialItem();
    this.sincronizarDatasFormComItensSeVazio();

    this.linhasImportacao = [];
    this.arquivoImportacao = null;

    if (modal) {
      modal.close();
    }

    this.toastr.success('', `${adicionados} produto(s) importado(s) da planilha!`);
  }

  trackByProdutoId(index: number, item: Produto): number {
    return item && item.id ? item.id : index;
  }

  trackByClienteId(index: number, item: Cliente): number {
    return item && item.id ? item.id : index;
  }

  trackByVendedorId(index: number, item: Vendedor): number {
    return item && item.id ? item.id : index;
  }

  abrirModalProdutosAdicionadosEmMassa(content: any) {
    // Pré-calcular lista de produtos selecionados com detalhes (O(N) em vez de O(N*M))
    this.produtosSelecionadosDetalhes = Array.from(this.produtosSelecionadosEmMassa)
      .map(id => this.produtosPorId.get(id))
      .filter(p => p != null);

    this.modalService.open(content, {
      ariaLabelledBy: "titulo-produtos-adicionados-em-massa",
      size: "lg",
      backdrop: "static"
    });
  }

  /* Regras de Grupo */

  carregarRegrasGrupo() {
    if (this.produtoTabelaEspecial == null) {
      this.regrasGrupoCarregando = false;
      return;
    }
    this.regrasGrupoCarregando = true;
    const guid = localStorage.getItem('guidclientenapis');
    this.produtoService.getRegraGrupos(this.produtoTabelaEspecial.idprodutotabelaespecial, 'tabela_especial', guid).subscribe(r => {
      this.regrasGrupo = r;
      this.regrasGrupoCarregando = false;
    });
  }

  carregarValoresCliente() {
    if (!this.rgCampoCliente) return;
    this.rgValoresCliente = [];
    this.rgValorCliente = '';
    this.rgValoresClienteCarregando = true;
    const guid = localStorage.getItem('guidclientenapis');
    this.produtoService.getValoresDistintos('cliente', this.rgCampoCliente, guid).subscribe(v => {
      this.rgValoresCliente = v;
      this.rgValoresClienteCarregando = false;
    });
  }

  carregarValoresProduto() {
    if (!this.rgCampoProduto) return;
    this.rgValoresProduto = [];
    this.rgValorProduto = '';
    this.rgValoresProdutoCarregando = true;
    const guid = localStorage.getItem('guidclientenapis');
    this.produtoService.getValoresDistintos('produto', this.rgCampoProduto, guid).subscribe(v => {
      this.rgValoresProduto = v;
      this.rgValoresProdutoCarregando = false;
    });
  }

  adicionarRegraGrupoCliente() {
    if (!this.rgCampoCliente || !this.rgValorCliente) return;
    if (this.regrasGrupoClientes.some(r => r.campo === this.rgCampoCliente && r.valor === this.rgValorCliente)) {
      this.toastr.warning('', 'Essa regra já foi adicionada.');
      return;
    }
    const regra = new PromoRegraGrupo();
    regra.promo_id = this.produtoTabelaEspecial?.idprodutotabelaespecial ?? 0;
    regra.promo_tipo = 'tabela_especial';
    regra.entidade = 'cliente';
    regra.campo = this.rgCampoCliente;
    regra.valor = this.rgValorCliente;
    regra.guidclientenapis = localStorage.getItem('guidclientenapis');

    if (this.produtoTabelaEspecial == null) {
      regra.id = this._nextTempId--;
      this.regrasGrupo = [...this.regrasGrupo, regra];
      this.toastr.success('', 'Regra de cliente adicionada!');
      this.rgCampoCliente = '';
      this.rgValorCliente = '';
      this.rgValoresCliente = [];
      return;
    }

    this.produtoService.addRegraGrupo(regra).subscribe(ok => {
      if (ok) {
        this.toastr.success('', 'Regra de cliente adicionada!');
        this.rgCampoCliente = '';
        this.rgValorCliente = '';
        this.rgValoresCliente = [];
        this.carregarRegrasGrupo();
      } else {
        this.toastr.error('', 'Erro ao adicionar regra.');
      }
    });
  }

  adicionarRegraGrupoProduto() {
    if (!this.rgCampoProduto || !this.rgValorProduto || !this.rgQtdProduto || !this.rgTipoProduto || this.rgPercentualProduto == null) return;
    if (this.regrasGrupoProdutos.some(r => r.campo === this.rgCampoProduto && r.valor === this.rgValorProduto)) {
      this.toastr.warning('', 'Essa regra já foi adicionada.');
      return;
    }
    const regra = new PromoRegraGrupo();
    regra.promo_id    = this.produtoTabelaEspecial?.idprodutotabelaespecial ?? 0;
    regra.promo_tipo  = 'tabela_especial';
    regra.entidade    = 'produto';
    regra.campo       = this.rgCampoProduto;
    regra.valor       = this.rgValorProduto;
    regra.qtd         = this.rgQtdProduto;
    regra.tipo_preco  = this.rgTipoProduto;
    regra.percentual  = this.rgPercentualProduto;
    regra.guidclientenapis = localStorage.getItem('guidclientenapis');

    if (this.produtoTabelaEspecial == null) {
      regra.id = this._nextTempId--;
      this.regrasGrupo = [...this.regrasGrupo, regra];
      this.toastr.success('', 'Regra de produto adicionada!');
      this.rgCampoProduto       = '';
      this.rgValorProduto       = '';
      this.rgValoresProduto     = [];
      this.rgQtdProduto         = null;
      this.rgTipoProduto        = '';
      this.rgPercentualProduto  = null;
      return;
    }

    this.produtoService.addRegraGrupo(regra).subscribe(ok => {
      if (ok) {
        this.toastr.success('', 'Regra de produto adicionada!');
        this.rgCampoProduto       = '';
        this.rgValorProduto       = '';
        this.rgValoresProduto     = [];
        this.rgQtdProduto         = null;
        this.rgTipoProduto        = '';
        this.rgPercentualProduto  = null;
        this.carregarRegrasGrupo();
      } else {
        this.toastr.error('', 'Erro ao adicionar regra.');
      }
    });
  }

  removerRegraGrupo(id: number) {
    if (this.produtoTabelaEspecial == null || id < 0) {
      this.regrasGrupo = this.regrasGrupo.filter(r => r.id !== id);
      this.toastr.success('', 'Regra removida!');
      return;
    }
    this.produtoService.deleteRegraGrupo(id).subscribe(ok => {
      if (ok) {
        this.toastr.success('', 'Regra removida!');
        this.carregarRegrasGrupo();
      } else {
        this.toastr.error('', 'Erro ao remover regra.');
      }
    });
  }

  private persistirRegrasGrupo(idPromoção: number): Observable<void> {
    const regrasPendentes = this.regrasGrupo.filter(r => !r.id || r.id <= 0);
    if (regrasPendentes.length === 0) {
      return of(void 0);
    }
    regrasPendentes.forEach(r => r.promo_id = idPromoção);
    return from(regrasPendentes).pipe(
      concatMap(regra => this.produtoService.addRegraGrupo(regra)),
      toArray(),
      map(() => void 0)
    );
  }

  /* ===== Log de uso da cota (promoção) ===== */

  // Abre o modal com o log individual de uso de um item (cota).
  abrirLogItem(produto: ProdutoTabelaEspecialItens) {
    if (produto == null || produto.idprodutoespecial == null) {
      this.toastr.info("", "Item sem cota para exibir log.");
      return;
    }
    this.logItemProdutoNome = produto.name || produto.codigo || ("Item " + produto.idprodutoespecial);
    this.logItemLinhas = [];
    this.carregandoLogItem = true;
    this.modalService.open(this.modalLogItem, { size: "lg", backdrop: "static", scrollable: true });
    this.promocoesService.getUsoCotaItem(produto.idprodutoespecial).subscribe(linhas => {
      this.logItemLinhas = linhas || [];
      this.carregandoLogItem = false;
    });
  }

  // Abre o modal de diagnóstico da promoção inteira (carrega tudo e filtra no front).
  abrirDiagnostico() {
    if (this.produtoTabelaEspecial == null || this.produtoTabelaEspecial.idprodutotabelaespecial == null) {
      this.toastr.info("", "Salve a promoção antes de ver o diagnóstico.");
      return;
    }
    this.diagnosticoLinhas = [];
    this.diagnosticoFiltradas = [];
    this.diagnosticoVendedores = [];
    this.diagnosticoClientes = [];
    this.diagFiltroSeller = null;
    this.diagFiltroClient = null;
    this.carregandoDiagnostico = true;
    this.modalService.open(this.modalDiagnostico, { size: "xl", backdrop: "static", scrollable: true });
    this.promocoesService.getDiagnosticoUsoPromocao(this.produtoTabelaEspecial.idprodutotabelaespecial, null, null).subscribe(linhas => {
      this.diagnosticoLinhas = linhas || [];
      this.montarOpcoesDiagnostico();
      this.aplicarFiltroDiagnostico();
      this.carregandoDiagnostico = false;
    });
  }

  // Monta as listas de vendedores/clientes distintos a partir do dataset.
  private montarOpcoesDiagnostico() {
    const vMap = new Map<number, string>();
    const cMap = new Map<number, string>();
    for (const l of this.diagnosticoLinhas) {
      if (l.seller_id != null) { vMap.set(l.seller_id, l.seller_nome); }
      if (l.client_id != null) { cMap.set(l.client_id, l.client_nome); }
    }
    this.diagnosticoVendedores = Array.from(vMap, ([id, nome]) => ({ id, nome })).sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
    this.diagnosticoClientes = Array.from(cMap, ([id, nome]) => ({ id, nome })).sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
  }

  // Aplica os filtros de vendedor/cliente (client-side).
  aplicarFiltroDiagnostico() {
    this.diagnosticoFiltradas = this.diagnosticoLinhas.filter(l =>
      (this.diagFiltroSeller == null || l.seller_id === Number(this.diagFiltroSeller)) &&
      (this.diagFiltroClient == null || l.client_id === Number(this.diagFiltroClient))
    );
  }

  // Totais do diagnóstico (sobre o conjunto filtrado).
  get diagnosticoQtdTotal(): number {
    return this.diagnosticoFiltradas.reduce((acc, l) => acc + (Number(l.quantidade) || 0), 0);
  }

  get diagnosticoQtdPedidos(): number {
    return new Set(this.diagnosticoFiltradas.map(l => l.order_id_app)).size;
  }

  get diagnosticoQtdClientes(): number {
    return new Set(this.diagnosticoFiltradas.filter(l => l.client_id != null).map(l => l.client_id)).size;
  }
}
